import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { ClipboardPlus, ClipboardList, Users, CalendarDays, Sparkles, Settings as SettingsIcon, BarChart3 } from 'lucide-react';
import { AuthContext } from '../App';
import SliderAssessment from './SliderAssessment';
import PlayersRole from './PlayersRole';
import CricAITab from './CricAITab';
import CoachCalendar from './CoachCalendar';
import PerformanceConfig from './PerformanceConfig';
import ServicesConfig from './ServicesConfig';
import WorkspaceSidebar from './WorkspaceSidebar';
import api from '../utils/api';
import logo from '../assets/logo.jpeg';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const resolveProfilePicUrl = (url) => {
    if (!url) return null;
    if (/^https?:\/\//i.test(url) || url.startsWith('data:') || url.startsWith('blob:')) return url;
    const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    if (!base) return url;
    return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
  };
  const [activeTab, setActiveTab] = useState('new-assessment');
  const [players, setPlayers] = useState([]);
  const [performanceQuestions, setPerformanceQuestions] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Lazy loading state
  const [allPlayers, setAllPlayers] = useState([]);
  const [displayedPlayers, setDisplayedPlayers] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMorePlayers, setHasMorePlayers] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const observerRef = useRef();
  const PLAYERS_PER_PAGE = 20;

  // Assessments tab state
  const [assessmentsData, setAssessmentsData] = useState([]);
  const [selectedPlayerHistory, setSelectedPlayerHistory] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [filterSkillLevel, setFilterSkillLevel] = useState('all');
  const [skillLevels, setSkillLevels] = useState([]);

  const [newAssessment, setNewAssessment] = useState({
    kid_id: '',
    assessment_date: new Date().toISOString().split('T')[0],
    assessment_type: 'regular',
    overall_comments: '',
    scores: [],
    area_comments: {}
  });

  // State for mobile collapsible areas (will be populated dynamically)
  const [expandedAreas, setExpandedAreas] = useState({});

  // State for expanded skill levels in goals section
  const [expandedSkillLevels, setExpandedSkillLevels] = useState({});
  
  // State for expanded goals management skill levels
  const [expandedGoalsSkillLevels, setExpandedGoalsSkillLevels] = useState({});

  // Function to get background gradient for skill levels
  const getSkillLevelGradient = (skillLevelName) => {
    const gradients = {
      'Budding': 'linear-gradient(135deg, #16a085 0%, #27ae60 50%, #2ecc71 100%)', // Green
      'Budding+': 'linear-gradient(135deg, #2980b9 0%, #3498db 50%, #5dade2 100%)', // Blue
      'Rising': 'linear-gradient(135deg, #8e44ad 0%, #9b59b6 50%, #bb8fce 100%)', // Purple
      'Shining': 'linear-gradient(135deg, #e67e22 0%, #f39c12 50%, #f7dc6f 100%)', // Orange
      'Elite': 'linear-gradient(135deg, #c0392b 0%, #e74c3c 50%, #ec7063 100%)', // Red
      'Elite+': 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)' // Dark Blue
    };
    return gradients[skillLevelName] || 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)';
  };

  // State for category goals and averages
  const [categoryGoals, setCategoryGoals] = useState([]);
  const [skillLevelAverages, setSkillLevelAverages] = useState({});

  // Assessment stats (quarter/month/coach)
  const [assessmentStats, setAssessmentStats] = useState(null);
  const [assessmentsLoaded, setAssessmentsLoaded] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Settings tab state
  const [activeSettingsTab, setActiveSettingsTab] = useState('performance-management');
  const [performanceAreas, setPerformanceAreas] = useState([]);
  const [allPerformanceQuestions, setAllPerformanceQuestions] = useState([]);
  const [skillLevelGoals, setSkillLevelGoals] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');

  // Kids management state
  const [allKids, setAllKids] = useState([]);
  const [filterKidsStatus, setFilterKidsStatus] = useState('all');

  // User search state
  const [userSearchTerm, setUserSearchTerm] = useState('');
  
  // Kids search state
  const [kidsSearchTerm, setKidsSearchTerm] = useState('');

  // Unified members management state
  const [membersSearchTerm, setMembersSearchTerm] = useState('');
  const [membersFilter, setMembersFilter] = useState('all');
  const [expandedMemberRows, setExpandedMemberRows] = useState({});

  // Rate override modal state
  const [showRateOverrideModal, setShowRateOverrideModal] = useState(false);
  const [rateOverrideKid, setRateOverrideKid] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'assessments' && !assessmentsLoaded) {
      fetchAssessmentsData();
    } else if (activeTab === 'settings' && !settingsLoaded) {
      fetchSettingsData();
    }
  }, [activeTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [
        playersResponse,
        questionsResponse
      ] = await Promise.all([
        api.get('/api/kids'),  // Get all approved kids for admin/coach
        api.get('/api/performance-questions')
      ]);
      
      const allPlayersData = playersResponse.data.kids || [];
      // Filter to only show approved kids for assessments
      const approvedKids = allPlayersData.filter(kid => kid.is_approved);
      setAllPlayers(approvedKids);
      setPlayers(approvedKids);
      
      // For new assessment tab, show all players immediately
      setDisplayedPlayers(allPlayersData);
      setCurrentPage(0);
      setHasMorePlayers(false); // Disable pagination for consistency
      
      setPerformanceQuestions(questionsResponse.data.questions || []);
      
      // Initialize scores for new assessment
      const initialScores = questionsResponse.data.questions.map(q => ({
        question_id: q.id,
        score: ''
      }));
      setNewAssessment(prev => ({ ...prev, scores: initialScores }));
      
    } catch (error) {
      console.error('AdminDashboard: Error fetching data:', error);
      setError(error.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Lazy loading function
  const loadMorePlayers = useCallback(() => {
    if (loadingMore || !hasMorePlayers) return;
    
    setLoadingMore(true);
    const nextPage = currentPage + 1;
    const startIndex = nextPage * PLAYERS_PER_PAGE;
    const endIndex = startIndex + PLAYERS_PER_PAGE;
    const newPlayers = allPlayers.slice(startIndex, endIndex);
    
    setTimeout(() => {
      setDisplayedPlayers(prev => [...prev, ...newPlayers]);
      setCurrentPage(nextPage);
      setHasMorePlayers(endIndex < allPlayers.length);
      setLoadingMore(false);
    }, 300); // Small delay for better UX
  }, [currentPage, loadingMore, hasMorePlayers, allPlayers]);

  // Intersection Observer for lazy loading
  const lastPlayerElementRef = useCallback(node => {
    if (loadingMore) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMorePlayers) {
        loadMorePlayers();
      }
    });
    if (node) observerRef.current.observe(node);
  }, [loadingMore, hasMorePlayers, loadMorePlayers]);

  const calculateSkillLevelAverages = (playersData, goalsData) => {
    const averagesByLevel = {};
    
    // First, initialize all skill levels with their targets from goals data
    const skillLevelIds = [...new Set(goalsData.map(goal => goal.skill_category_id))];
    skillLevelIds.forEach(levelId => {
      averagesByLevel[levelId] = {
        overall: 0,
        areas: {},
        targets: {}
      };
      
      // Get targets from category goals for this skill level
      const levelGoals = goalsData.filter(goal => goal.skill_category_id == levelId);
      levelGoals.forEach(goal => {
        averagesByLevel[levelId].targets[goal.area_name] = goal.target_score;
      });
    });
    
    // Group players by skill level
    const playersByLevel = {};
    playersData.forEach(player => {
      const levelId = player.skill_category_id;
      if (!playersByLevel[levelId]) {
        playersByLevel[levelId] = [];
      }
      playersByLevel[levelId].push(player);
    });
    
    // Calculate averages for skill levels that have players
    Object.entries(playersByLevel).forEach(([levelId, players]) => {
      // Ensure the level exists in averagesByLevel (it should from the initialization above)
      if (!averagesByLevel[levelId]) {
        averagesByLevel[levelId] = {
          overall: 0,
          areas: {},
          targets: {}
        };
      }
      
      // Calculate overall average
      const overallScores = players
        .filter(p => p.overall_average !== null && p.overall_average !== undefined && !isNaN(Number(p.overall_average)))
        .map(p => Number(p.overall_average));
      
      if (overallScores.length > 0) {
        averagesByLevel[levelId].overall = overallScores.reduce((sum, score) => sum + score, 0) / overallScores.length;
      }
      
      // Calculate averages by performance area
      const areaNames = performanceAreas.map(area => area.name) || ['Batting', 'Bowling', 'Fielding', 'Fitness', 'Attitude', 'Wicketkeeping'];
      areaNames.forEach(areaName => {
        // Performance areas data from backend uses lowercase, so convert for lookup
        const lowerAreaName = areaName.toLowerCase();
        const areaScores = players
          .filter(p => p.performance_areas && p.performance_areas[lowerAreaName])
          .map(p => p.performance_areas[lowerAreaName].current_avg)
          .filter(score => score !== null && score !== undefined && !isNaN(Number(score)))
          .map(score => Number(score));
        
        if (areaScores.length > 0) {
          averagesByLevel[levelId].areas[areaName] = areaScores.reduce((sum, score) => sum + score, 0) / areaScores.length;
        }
      });
    });
    
    return averagesByLevel;
  };

  const fetchAssessmentsData = async () => {
    try {
      setTabLoading(true);
      setError('');
      
      const [
        assessmentsResponse,
        skillLevelsResponse,
        categoryGoalsResponse,
        performanceAreasResponse,
        statsResponse
      ] = await Promise.all([
        api.get('/api/players-with-assessments?limit=1000'),
        api.get('/api/skill-levels'),
        api.get('/api/category-goals'),
        api.get('/api/performance_areas'),
        api.get('/api/assessment-stats').catch(() => ({ data: null }))
      ]);
      
      // Set assessment stats (lightweight)
      if (statsResponse.data) {
        setAssessmentStats(statsResponse.data);
      }
      
      // Set performance areas for target display
      const performanceAreasData = performanceAreasResponse.data.performance_areas || [];
      setPerformanceAreas(performanceAreasData);
      
      const playersData = assessmentsResponse.data.players || [];
      
      // Process category goals and calculate skill level averages
      const goalsData = categoryGoalsResponse.data.goals || [];
      setCategoryGoals(goalsData);
      
      // Calculate averages for each skill level and performance area
      const averagesByLevel = calculateSkillLevelAverages(playersData, goalsData);
      setSkillLevelAverages(averagesByLevel);
      
      setAllPlayers(playersData);
      setDisplayedPlayers(playersData);
      setCurrentPage(0);
      setHasMorePlayers(false);
      
      setAssessmentsData(playersData);
      const skillLevelsData = skillLevelsResponse.data.skill_levels || [];
      setSkillLevels(skillLevelsData);
      
      // Collapse all skill levels by default for faster initial render
      const expandedLevels = {};
      skillLevelsData.forEach(level => {
        expandedLevels[level.id] = false;
      });
      setExpandedSkillLevels(expandedLevels);
      setAssessmentsLoaded(true);
      
    } catch (error) {
      console.error('Error fetching assessments data:', error);
      setError(error.response?.data?.error || 'Failed to load assessments data');
    } finally {
      setTabLoading(false);
    }
  };

  const fetchPlayerHistory = async (playerId) => {
    try {
      console.log('Fetching player history for ID:', playerId);
      const response = await api.get(`/api/assessments/player/${playerId}/history`);
      console.log('Player history response:', response.data);
      setSelectedPlayerHistory(response.data);
      setShowHistoryModal(true);
    } catch (error) {
      console.error('Error fetching player history:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      setError(`Failed to load player history: ${error.response?.data?.error || error.message}`);
    }
  };

  const getScoreClass = (score) => {
    const numScore = Number(score);
    if (numScore >= 8) return 'score-excellent';
    if (numScore >= 6) return 'score-good';
    if (numScore >= 4) return 'score-average';
    return 'score-poor';
  };

  const fetchSettingsData = async (preserveMessages = false) => {
    try {
      setTabLoading(true);
      if (!preserveMessages) {
        setError('');
      }
      
      // Use the same API endpoints for both coaches and admins
      // Authorization will be handled on the backend
      
      const apiCalls = [
        api.get('/api/performance_areas'),
        api.get('/api/performance-questions'),
        api.get('/api/category-goals'),
        api.get('/api/skill-levels')
      ];
      
      // Only admins need user management data
      if (user?.user_type !== 'coach') {
        apiCalls.push(
          api.get('/api/users'),
          api.get('/api/pending-users'),
          api.get('/api/kids')
        );
      }
      
      const responses = await Promise.all(apiCalls);
      
      const [
        areasResponse,
        questionsResponse,
        goalsResponse,
        skillLevelsResponse,
        usersResponse,
        pendingResponse,
        kidsResponse
      ] = responses;
      
      const performanceAreasData = areasResponse.data.performance_areas || [];
      setPerformanceAreas(performanceAreasData);
      
      // Set all performance areas as expanded by default
      const expandedAreasObj = {};
      performanceAreasData.forEach(area => {
        expandedAreasObj[area.id] = true;
      });
      setExpandedAreas(expandedAreasObj);
      
      setAllPerformanceQuestions(questionsResponse.data.questions || []);
      setSkillLevelGoals(goalsResponse.data.skill_level_goals || []);
      const skillLevelsData = skillLevelsResponse.data.skill_levels || [];
      setSkillLevels(skillLevelsData);
      
      // Initialize goals skill levels to be expanded by default
      const expandedGoalsLevels = {};
      skillLevelsData.forEach(level => {
        expandedGoalsLevels[level.id] = true;
      });
      setExpandedGoalsSkillLevels(expandedGoalsLevels);
      
      // Only set user management data for admins
      if (user?.user_type !== 'coach') {
        const allUsersData = usersResponse?.data.users || [];
        setAllUsers(allUsersData);
        // Extract coaches from users data
        setCoaches(allUsersData.filter(u => u.user_type === 'coach'));
        setPendingUsers(pendingResponse?.data.pending_users || []);
        setAllKids(kidsResponse?.data.kids || []);
      } else {
        // For coaches, initialize empty arrays
        setAllUsers([]);
        setCoaches([]);
        setPendingUsers([]);
        setAllKids([]);
      }
      setSettingsLoaded(true);
      
    } catch (error) {
      console.error('Error fetching settings data:', error);
      if (!preserveMessages) {
        setError(error.response?.data?.error || 'Failed to load settings data');
      }
    } finally {
      setTabLoading(false);
    }
  };

  const handleCreateAssessment = async (e) => {
    if (e) e.preventDefault();
    try {
      setError('');
      setSuccess('');
      
      // Validate required fields
      if (!newAssessment.kid_id) {
        setError('Please select a player');
        return;
      }
      
      // Filter out empty scores and validate
      const validScores = newAssessment.scores.filter(score => 
        score.score !== '' && score.score >= 0 && score.score <= 10
      );
      
      if (validScores.length === 0) {
        setError('Please provide at least one score');
        return;
      }
      
      const assessmentData = {
        kidId: newAssessment.kid_id,  // Convert to camelCase for backend
        assessorId: user.id,           // Add current user as assessor
        assessment_date: newAssessment.assessment_date,
        assessment_type: newAssessment.assessment_type,
        overall_comments: newAssessment.overall_comments,
        area_comments: newAssessment.area_comments,
        scores: validScores
      };
      
      await api.post('/api/assessments', assessmentData);
      setSuccess('Assessment created successfully!');
      
      // Refresh assessments data to show the new assessment
      await fetchAssessmentsData();
      
      // Reset form
      setNewAssessment({
        kid_id: '',
        assessment_date: new Date().toISOString().split('T')[0],
        assessment_type: 'regular',
        overall_comments: '',
        scores: performanceQuestions.map(q => ({
          question_id: q.id,
          score: ''
        })),
        area_comments: {}
      });
      setSelectedPlayer(null);
      
    } catch (error) {
      console.error('Error creating assessment:', error);
      setError(error.response?.data?.error || 'Failed to create assessment');
    }
  };

  // Function to fetch latest assessment for a player
  const fetchLatestAssessment = async (playerId) => {
    try {
      const response = await api.get(`/api/assessments/player/${playerId}/history`);
      const assessments = response.data.assessments || [];
      
      if (assessments.length > 0) {
        // Get the latest assessment (first one since they're ordered by date DESC)
        const latestAssessment = assessments[0];
        return latestAssessment.detailed_scores || [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching latest assessment:', error);
      return [];
    }
  };

  const handlePlayerSelect = async (playerId) => {
    const player = players.find(p => p.id === parseInt(playerId));
    setSelectedPlayer(player);
    setNewAssessment(prev => ({ ...prev, kid_id: playerId }));
    
    // Fetch latest assessment scores for this player
    if (playerId) {
      const latestScores = await fetchLatestAssessment(playerId);
      
      // Update the assessment scores with latest values or default to 0
      const updatedScores = performanceQuestions.map(question => {
        const existingScore = latestScores.find(score => 
          score.question === question.question_text
        );
        
        return {
          question_id: question.id,
          score: existingScore ? existingScore.score : 0
        };
      });
      
      setNewAssessment(prev => ({ 
        ...prev, 
        scores: updatedScores,
        overall_comments: '', // Reset comments for new assessment
        area_comments: {} // Reset area comments
      }));
    }
  };

  const handleScoreChange = (questionId, field, value) => {
    if (questionId === 'overall_comments') {
      // Handle overall comments
      setNewAssessment(prev => ({
        ...prev,
        overall_comments: value
      }));
    } else {
      // Handle individual question scores
      setNewAssessment(prev => ({
        ...prev,
        scores: prev.scores.map(score =>
          score.question_id === questionId
            ? { ...score, [field]: value }
            : score
        )
      }));
    }
  };

  const handleAreaCommentChange = (area, value) => {
    setNewAssessment(prev => ({
      ...prev,
      area_comments: {
        ...prev.area_comments,
        [area]: value
      }
    }));
  };

  // Toggle area expansion for mobile
  const toggleAreaExpansion = (area) => {
    setExpandedAreas(prev => ({
      ...prev,
      [area]: !prev[area]
    }));
  };

  // Group questions by performance area
  const groupedQuestions = performanceQuestions.reduce((groups, question) => {
    const area = question.area_name || 'General';
    if (!groups[area]) {
      groups[area] = [];
    }
    groups[area].push(question);
    return groups;
  }, {});

  // Scoring guide data
  const scoringGuide = [
    { range: '9-10', label: 'Excellent', description: 'Outstanding performance, exceeds expectations', color: '#10b981' },
    { range: '7-8', label: 'Good', description: 'Strong performance, meets expectations', color: '#3b82f6' },
    { range: '5-6', label: 'Average', description: 'Satisfactory performance, room for improvement', color: '#f59e0b' },
    { range: '3-4', label: 'Below Average', description: 'Needs significant improvement', color: '#ef4444' },
    { range: '0-2', label: 'Poor', description: 'Requires immediate attention and training', color: '#dc2626' }
  ];

  // Settings handler functions
  const handleDeletePerformanceArea = async (areaId) => {
    if (!window.confirm('Are you sure you want to delete this performance area?')) return;
    
    try {
      await api.delete(`/api/performance_areas/${areaId}`);
      setSuccess('Performance area deleted successfully');
      fetchSettingsData();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to delete performance area');
    }
  };

  const handleDeletePerformanceQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this performance question?')) return;
    
    try {
      await api.delete(`/api/performance-questions/${questionId}`);
      setSuccess('Performance question deleted successfully');
      fetchSettingsData();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to delete performance question');
    }
  };

  const handleDeleteSkillGoal = async (goalId) => {
    if (!window.confirm('Are you sure you want to delete this skill level goal?')) return;
    
    try {
      await api.delete(`/api/category-goals/${goalId}`);
      setSuccess('Skill level goal deleted successfully');
      fetchSettingsData();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to delete skill level goal');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user? They will be marked as deactivated.')) return;
    
    try {
      await api.delete(`/api/users/${userId}`);
      setSuccess('User deactivated successfully');
      fetchSettingsData();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to deactivate user');
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      await api.put(`/api/approve-user/${userId}`);
      setSuccess('User approved successfully');
      fetchSettingsData();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to approve user');
    }
  };

  const handleRejectUser = async (userId) => {
    if (!window.confirm('Are you sure you want to reject this user registration?')) return;
    
    try {
      await api.put(`/api/reject-user/${userId}`);
      setSuccess('User rejected successfully');
      fetchSettingsData();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to reject user');
    }
  };

  // Filter users based on search term
  const filterUsers = (users) => {
    if (!userSearchTerm.trim()) return users;
    
    const searchLower = userSearchTerm.toLowerCase();
    return users.filter(user => {
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
      const email = (user.email || '').toLowerCase();
      const phone = (user.phone_number || '').toLowerCase();
      
      return fullName.includes(searchLower) || 
             email.includes(searchLower) || 
             phone.includes(searchLower);
    });
  };

  // Filter kids based on search term
  const filterKids = (kids) => {
    if (!kidsSearchTerm.trim()) return kids;
    
    const searchLower = kidsSearchTerm.toLowerCase();
    return kids.filter(kid => {
      const fullName = `${kid.first_name || ''} ${kid.last_name || ''}`.toLowerCase();
      const parentName = `${kid.parent_first_name || ''} ${kid.parent_last_name || ''}`.toLowerCase();
      const parentEmail = (kid.parent_email || '').toLowerCase();
      const skillLevel = (kid.skill_category_name || '').toLowerCase();
      
      return fullName.includes(searchLower) || 
             parentName.includes(searchLower) || 
             parentEmail.includes(searchLower) ||
             skillLevel.includes(searchLower);
    });
  };

  const handleSaveItem = async (type, data) => {
    try {
      setLoading(true);
      let response;

      switch (type) {
        case 'performance-area':
          if (editingItem && editingItem.id) {
            response = await api.put(`/api/performance_areas/${editingItem.id}`, data);
          } else {
            response = await api.post('/api/performance_areas', data);
          }
          break;

        case 'performance-question':
          // Debug: Log the data being sent
          console.log('Sending performance-question data:', data);
          if (editingItem && editingItem.id) {
            response = await api.put(`/api/performance-questions/${editingItem.id}`, data);
          } else {
            // If editingItem exists but has no ID, log this for debugging
            if (editingItem && !editingItem.id) {
              console.warn('EditingItem exists but has no ID, creating new item instead:', editingItem);
            }
            response = await api.post('/api/performance-questions', data);
          }
          break;

        case 'skill-goal':
          // Convert performance area name to ID
          const performanceArea = performanceAreas.find(area => area.name === data.performance_area_name);
          if (!performanceArea) {
            throw new Error('Invalid performance area selected');
          }
          
          const skillGoalData = {
            skill_level_id: parseInt(data.skill_level_id),
            performance_area_id: performanceArea.id,
            goal_score: parseFloat(data.goal_score)
          };
          
          console.log('Sending skill goal data:', skillGoalData);
          console.log('Editing item:', editingItem);
          
          if (editingItem && editingItem.id) {
            console.log(`Making PUT request to /api/category-goals/${editingItem.id}`);
            response = await api.put(`/api/category-goals/${editingItem.id}`, skillGoalData);
            console.log('PUT response:', response);
          } else {
            console.log('Making POST request to /api/category-goals');
            response = await api.post('/api/category-goals', skillGoalData);
            console.log('POST response:', response);
          }
          break;

        case 'coach':
        case 'parent':
          if (editingItem && editingItem.id) {
            response = await api.put(`/api/users/${editingItem.id}`, { ...data, user_type: type === 'coach' ? 'coach' : 'parent' });
          } else {
            response = await api.post(`/api/users`, { ...data, user_type: type === 'coach' ? 'coach' : 'parent' });
          }
          break;

        case 'user':
          if (editingItem && editingItem.id) {
            response = await api.put(`/api/users/${editingItem.id}`, data);
          } else {
            // For new users, use the unified /api/users endpoint with user_type
            response = await api.post(`/api/users`, data);
          }
          break;

        case 'kid':
          // For kids, send both is_active and is_approved as separate fields
          const kidData = {
            ...data,
            is_active: data.is_active,
            is_approved: data.is_approved
          };
          
          if (editingItem && editingItem.id) {
            response = await api.put(`/api/kids/${editingItem.id}`, kidData);
          } else {
            response = await api.post('/api/kids', kidData);
          }
          break;

        default:
          throw new Error('Unknown modal type');
      }

      const successMessage = `${type.replace('-', ' ')} ${editingItem ? 'updated' : 'created'} successfully${response.data?.generated_password ? `. Generated password: ${response.data.generated_password}` : ''}`;
      setSuccess(successMessage);
      setShowModal(false);
      setEditingItem(null);
      
      // Refresh data but preserve success message
      try {
        await fetchSettingsData(true); // Pass true to preserve messages
      } catch (fetchError) {
        console.error('Error refreshing data after save:', fetchError);
        // Don't override success message with fetch error
        // The save was successful, just refresh failed
      }

    } catch (error) {
      console.error('Error in handleSaveItem:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      
      const errorMessage = error.response?.data?.error || error.message || `Failed to ${editingItem ? 'update' : 'create'} ${type.replace('-', ' ')}`;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Kids management handler functions
  const handleApproveKid = async (kidId) => {
    try {
      console.log('Attempting to approve kid with ID:', kidId);
      const response = await api.put(`/api/kids/${kidId}/approve`);
      console.log('Approval response:', response);
      setSuccess('Kid approved successfully');
      fetchSettingsData();
    } catch (error) {
      console.error('Error approving kid:', error);
      console.error('Error response:', error.response);
      setError(error.response?.data?.error || 'Failed to approve kid');
    }
  };

  const handleDeleteKid = async (kidId) => {
    if (!window.confirm('Are you sure you want to deactivate this player? They will be marked as deactivated.')) return;
    
    try {
      setLoading(true);
      setError('');
      await api.delete(`/api/kids/${kidId}`);
      setSuccess('Player deactivated successfully');
      fetchSettingsData();
    } catch (error) {
      console.error('Delete kid error:', error);
      
      let errorMessage = 'Failed to delete kid';
      
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 500) {
          // Internal server error - likely database constraint issue
          if (data.error && data.error.includes('constraint')) {
            errorMessage = 'Cannot delete this player because they have assessment records. Please remove all assessments first, or contact support for assistance.';
          } else if (data.error && data.error.includes('foreign key')) {
            errorMessage = 'Cannot delete this player because they are referenced by other records (assessments, etc.). Please remove related records first.';
          } else {
            errorMessage = `Server error (${status}): ${data.error || data.message || 'Unknown database error'}`;
          }
        } else if (status === 404) {
          errorMessage = 'Player not found - they may have already been deleted.';
        } else if (status === 403) {
          errorMessage = 'You do not have permission to delete this player.';
        } else {
          errorMessage = `Error (${status}): ${data.error || data.message || 'Unknown error'}`;
        }
      } else if (error.request) {
        // Network error
        errorMessage = 'Network error - please check your connection and try again.';
      } else {
        // Other error
        errorMessage = `Unexpected error: ${error.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="dash-loader">
          <div className="dash-loader-cricket">
            <div className="dash-loader-ball" />
            <div className="dash-loader-stumps">
              <span /><span /><span />
            </div>
          </div>
          <p className="dash-loader-text">Setting up the pitch<span className="dash-loader-dots" /></p>
        </div>
      </div>
    );
  }

  const isCoach = user?.user_type === 'coach';
  const sidebarItems = [
    { key: 'new-assessment', label: 'New Assessment', icon: ClipboardPlus },
    { key: 'assessments', label: isCoach ? 'Player Assessment' : 'Assessments', icon: ClipboardList },
    { key: 'players-role', label: 'Players Role', icon: Users },
    { key: 'calendar', label: 'Calendar', icon: CalendarDays },
    { key: 'cric-ai', label: 'Cric AI', icon: Sparkles },
    { key: 'settings', label: 'Settings', icon: SettingsIcon },
    ...(!isCoach ? [{ key: 'reports', label: 'Reports', icon: BarChart3, disabled: true }] : [])
  ];

  return (
    <div className="admin-dashboard workspace-shell">
      <WorkspaceSidebar
        showBrand={false}
        brandTitle={process.env.REACT_APP_ACADEMY_NAME || 'MSKICA'}
        brandSubtitle={isCoach ? 'Coach workspace' : 'Admin workspace'}
        items={sidebarItems}
        activeKey={activeTab}
        onSelect={setActiveTab}
        userName={user?.full_name || user?.username}
        userRole={user?.user_type}
        onLogout={logout}
      />
      <div className="workspace-content">
      {/* Brand + Header form one merged top band */}
      <div className="dash-topband">
      {/* Academy Brand Bar */}
      <div className="dash-brandbar">
        <img
          src={logo}
          alt="Academy logo"
          className="dash-brand-logo"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <div className="dash-brand-text">
          <span className="dash-brand-name">{process.env.REACT_APP_ACADEMY_NAME || 'MSK PRASAD\'s International Cricket Academy - Seattle'}</span>
          <span className="dash-brand-tagline">{process.env.REACT_APP_ACADEMY_TAGLINE || 'Performance Excellence'}</span>
        </div>
      </div>

      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <h1 className="dashboard-title">
            {isCoach ? '🏏 Coach Dashboard' : 'Admin Dashboard'}
          </h1>
          <div className="header-actions">
            <span className="user-info">Welcome, {user?.full_name || user?.username}</span>
            <button onClick={logout} className="logout-btn">Logout</button>
          </div>
        </div>
      </header>
      </div>

      {/* Main Content */}
      <main className="dashboard-main">
        {error && (
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            {error}
          </div>
        )}
        
        {success && (
          <div className="alert alert-success">
            <span className="alert-icon">✅</span>
            {success}
          </div>
        )}

        {activeTab === 'new-assessment' && (
          <div className="tab-content">
            <div className="section-header">
              <h2>Create New Assessment</h2>
              <p>Evaluate player performance across different skill areas</p>
            </div>

            <form onSubmit={handleCreateAssessment} className="assessment-form">
              {/* Player Selection */}
              <div className="form-section">
                <h3>👤 Player Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="player-select">Select Player *</label>
                    <select
                      id="player-select"
                      value={newAssessment.kid_id}
                      onChange={(e) => handlePlayerSelect(e.target.value)}
                      required
                    >
                      <option value="">Choose a player...</option>
                      {players.map(player => (
                        <option key={player.id} value={player.id}>
                          {player.first_name} {player.last_name} - {player.skill_category_name || 'No Category'}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="assessment-date">Assessment Date *</label>
                    <input
                      id="assessment-date"
                      type="date"
                      value={newAssessment.assessment_date}
                      onChange={(e) => setNewAssessment(prev => ({ ...prev, assessment_date: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="assessment-type">Assessment Type</label>
                    <select
                      id="assessment-type"
                      value={newAssessment.assessment_type}
                      onChange={(e) => setNewAssessment(prev => ({ ...prev, assessment_type: e.target.value }))}
                    >
                      <option value="regular">Regular Assessment</option>
                      <option value="trial">Trial Assessment</option>
                      <option value="progress">Progress Review</option>
                    </select>
                  </div>
                </div>

                {selectedPlayer && (
                  <div className="player-info-section">
                    <div className="player-details-grid">
                      <div className="player-avatar-large">
                        <span>{selectedPlayer.first_name?.charAt(0) || '?'}{selectedPlayer.last_name?.charAt(0) || '?'}</span>
                      </div>
                      <div className="player-main-info">
                        <h4>{selectedPlayer.first_name} {selectedPlayer.last_name}</h4>
                        <div className="player-meta-info">
                          <div className="player-meta-item">
                            <span className="player-meta-icon">🏆</span>
                            <span>Category: {selectedPlayer.skill_category_name || 'Category TBD'}</span>
                          </div>
                          <div className="player-meta-item">
                            <span className="player-meta-icon">📊</span>
                            <span>Level: {selectedPlayer.performance_level || 'regular'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="assessment-info">
                        <div className="assessment-date">{new Date(newAssessment.assessment_date).toLocaleDateString()}</div>
                        <div className="assessment-type">{newAssessment.assessment_type}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Performance Evaluation */}
              {performanceQuestions.length > 0 ? (
                <div className="form-section">
                  <SliderAssessment
                    performanceQuestions={performanceQuestions}
                    onScoreUpdate={handleScoreChange}
                    initialScores={{
                      ...newAssessment.scores.reduce((acc, score) => {
                        acc[score.question_id] = {
                          score: parseFloat(score.score) || 0,
                          comments: score.comments || ''
                        };
                        return acc;
                      }, {}),
                      overall_comments: newAssessment.overall_comments || ''
                    }}
                    areaComments={newAssessment.area_comments}
                    onAreaCommentChange={handleAreaCommentChange}
                    disabled={loading}
                    onCreateAssessment={handleCreateAssessment}
                  />
                </div>
              ) : (
                <div className="form-section">
                  <h3>🎯 Performance Evaluation</h3>
                  <div className="no-questions-message">
                    <p>⚠️ No performance questions loaded. Questions count: {performanceQuestions.length}</p>
                    <p>Loading state: {loading ? 'Loading...' : 'Loaded'}</p>
                  </div>
                </div>
              )}

            </form>
          </div>
        )}

        {activeTab === 'assessments' && (
          <div className="tab-content">
            {tabLoading ? (
              <div className="tab-loader">
                <div className="tab-loader-bar" />
                <div className="tab-skel">
                  <div className="tab-skel-hero" />
                  <div className="tab-skel-rows">
                    {[1,2,3,4,5].map(i => <div key={i} className="tab-skel-row" />)}
                  </div>
                </div>
              </div>
            ) : (
            <>
            <div className="asr-page">
              {/* Hero Banner */}
              <div className="asr-hero">
                <div className="asr-hero-shine" />
                <div className="asr-hero-content">
                  <div className="asr-hero-left">
                    <div className="asr-hero-badge">Performance Tracking</div>
                    <h2 className="asr-hero-title">Assessments</h2>
                  </div>
                  <div className="asr-hero-mid">
                    <div className="asr-stat">
                      <span className="asr-stat-num">{assessmentsData.length}</span>
                      <span className="asr-stat-label">Players</span>
                    </div>
                    <div className="asr-stat-divider" />
                    <div className="asr-stat">
                      <span className="asr-stat-num">{assessmentStats?.month ?? '—'}</span>
                      <span className="asr-stat-label">{assessmentStats?.month_label ?? 'This Month'}</span>
                    </div>
                    <div className="asr-stat-divider" />
                    <div className="asr-stat">
                      <span className="asr-stat-num">{assessmentStats?.quarter ?? '—'}</span>
                      <span className="asr-stat-label">{assessmentStats?.quarter_label ?? 'This Qtr'}</span>
                    </div>
                    <div className="asr-stat-divider" />
                    <div className="asr-stat">
                      <span className="asr-stat-num">{assessmentStats?.total ?? '—'}</span>
                      <span className="asr-stat-label">All Time</span>
                    </div>
                  </div>
                </div>

                {/* Coach-wise stats row */}
                {assessmentStats?.coaches && assessmentStats.coaches.length > 0 && (
                  <div className="asr-coach-row">
                    {assessmentStats.coaches.map((coach, i) => {
                      const pct = coach.quarter > 0 ? Math.round((coach.month / coach.quarter) * 100) : 0;
                      return (
                        <div key={i} className="asr-coach-card">
                          <div className="asr-coach-top">
                            <span className="asr-coach-avatar">{(coach.name || '?')[0]}</span>
                            <span className="asr-coach-name">{coach.name}</span>
                          </div>
                          <div className="asr-coach-bar-wrap">
                            <div className="asr-coach-bar-track">
                              <div className="asr-coach-bar-fill" style={{ width: `${pct}%` }} />
                            </div>
                            <div className="asr-coach-bar-labels">
                              <span className="asr-coach-lbl-mo">{coach.month} <small>this mo</small></span>
                              <span className="asr-coach-lbl-qtr">{coach.quarter} <small>this qtr</small></span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Score Legend */}
              <div className="asr-legend">
                <span className="asr-legend-item"><span className="asr-dot asr-dot-excellent"></span>8-10</span>
                <span className="asr-legend-item"><span className="asr-dot asr-dot-good"></span>6-7</span>
                <span className="asr-legend-item"><span className="asr-dot asr-dot-average"></span>4-5</span>
                <span className="asr-legend-item"><span className="asr-dot asr-dot-poor"></span>0-3</span>
              </div>

              {/* Skill Level Sections */}
              <div className="asr-levels">
                {loading ? (
                  <div className="asr-loading">
                    <div className="spinner"></div>
                    <p>Loading assessments...</p>
                  </div>
                ) : skillLevels.length === 0 ? (
                  <div className="asr-empty">
                    <span className="asr-empty-icon">📊</span>
                    <p>No skill levels configured yet.</p>
                  </div>
                ) : (
                  skillLevels.map(skillLevel => {
                    const playersInLevel = assessmentsData.filter(player => 
                      player.skill_category_id === skillLevel.id
                    );
                    const isExpanded = expandedSkillLevels[skillLevel.id];
                    const levelAverages = skillLevelAverages[skillLevel.id] || {};
                    const areas = performanceAreas.length > 0 ? performanceAreas : [{name: 'Batting'}, {name: 'Bowling'}, {name: 'Fielding'}, {name: 'Fitness'}, {name: 'Attitude'}, {name: 'Wicketkeeping'}];
                    
                    return (
                      <div key={skillLevel.id} className="asr-level-section">
                        {/* Level Header */}
                        <div 
                          className="asr-level-hdr"
                          onClick={() => setExpandedSkillLevels(prev => ({
                            ...prev,
                            [skillLevel.id]: !prev[skillLevel.id]
                          }))}
                          style={{ background: getSkillLevelGradient(skillLevel.name) }}
                        >
                          <div className="asr-level-left">
                            <span className="asr-level-dot" style={{ backgroundColor: skillLevel.color_code }}></span>
                            <h3 className="asr-level-name">{skillLevel.name}</h3>
                            <span className="asr-level-count">{playersInLevel.length}</span>
                            {assessmentStats?.skill_levels?.[skillLevel.id] && (
                              <div className="asr-level-stats">
                                <span className="asr-level-stat">
                                  <strong>{assessmentStats.skill_levels[skillLevel.id].month}</strong> <small>this mo</small>
                                </span>
                                <span className="asr-level-stat-sep">|</span>
                                <span className="asr-level-stat">
                                  <strong>{assessmentStats.skill_levels[skillLevel.id].quarter}</strong> <small>this qtr</small>
                                </span>
                              </div>
                            )}
                            {assessmentStats?.skill_levels?.[skillLevel.id]?.coaches?.length > 0 && (
                              <div className="asr-level-coaches">
                                {assessmentStats.skill_levels[skillLevel.id].coaches.map((c, ci) => (
                                  <span key={ci} className="asr-level-coach-chip">
                                    <span className="asr-level-coach-init">{(c.name || '?')[0]}</span>
                                    <span className="asr-level-coach-name">{c.name}</span>
                                    <span className="asr-level-coach-nums">{c.month}<small>mo</small> {c.quarter}<small>qtr</small></span>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="asr-level-chevron">{isExpanded ? '▾' : '▸'}</div>
                        </div>

                        {/* Target Row */}
                        {isExpanded && (
                          <div className="asr-target-row">
                            <div className="asr-target-label">Targets</div>
                            <div className="asr-target-scores">
                              {areas.map(areaObj => {
                                const targetValue = levelAverages.targets && levelAverages.targets[areaObj.name];
                                const target = targetValue && !isNaN(Number(targetValue))
                                  ? Number(targetValue).toFixed(1) : '—';
                                return (
                                  <div key={areaObj.name} className="asr-target-cell">
                                    <span className="asr-target-area">{areaObj.name.substring(0, 3).toUpperCase()}</span>
                                    <span className="asr-target-val">{target}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Player Rows */}
                        {isExpanded && (
                          <div className="asr-player-list">
                            {playersInLevel.length === 0 ? (
                              <div className="asr-no-players">No players in this level yet.</div>
                            ) : (
                              playersInLevel.map(player => {
                                const getAreaScoreClass = (score) => {
                                  const num = Number(score);
                                  if (num >= 8) return 'asr-sc-excellent';
                                  if (num >= 6) return 'asr-sc-good';
                                  if (num >= 4) return 'asr-sc-average';
                                  return 'asr-sc-poor';
                                };

                                return (
                                  <div key={player.id} className="asr-player-row">
                                    <div className="asr-player-main">
                                      {/* Avatar */}
                                      <div className="asr-avatar">
                                        {(player.first_name || '?')[0]}
                                      </div>

                                      {/* Player Info */}
                                      <div className="asr-player-info">
                                        <div className="asr-player-name">{player.first_name} {player.last_name}</div>
                                        <div className="asr-player-meta">
                                          {player.latest_assessment_date ? (
                                            <span className="asr-date-pill">
                                              {new Date(player.latest_assessment_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                            </span>
                                          ) : (
                                            <span className="asr-pill-none">No assessment</span>
                                          )}
                                          <span className={`asr-overall ${player.overall_average && !isNaN(Number(player.overall_average)) ? getAreaScoreClass(player.overall_average) : ''}`}>
                                            {player.overall_average && !isNaN(Number(player.overall_average)) ? Number(player.overall_average).toFixed(1) : '—'}
                                          </span>
                                        </div>
                                      </div>

                                      {/* Area Scores - radial gauges */}
                                      <div className="asr-scores-row">
                                        {player.latest_assessment_date ? areas.map(areaObj => {
                                          const area = areaObj.name.toLowerCase();
                                          const data = player.performance_areas?.[area] || null;
                                          const score = data?.current_avg && !isNaN(Number(data.current_avg)) ? Number(data.current_avg).toFixed(1) : null;
                                          const targetVal = levelAverages.targets && levelAverages.targets[areaObj.name];
                                          const meetsTarget = score && targetVal && Number(score) >= Number(targetVal);
                                          const pct = score ? (Number(score) / 10) * 100 : 0;
                                          const radius = 16;
                                          const circ = 2 * Math.PI * radius;
                                          const offset = circ - (pct / 100) * circ;
                                          const colorClass = score ? getAreaScoreClass(score) : 'asr-sc-na';
                                          const gradId = `gg-${player.id}-${areaObj.name.replace(/\s+/g, '')}`;
                                          const [gs, ge] = colorClass === 'asr-sc-excellent' ? ['#6ee7b7', '#059669']
                                            : colorClass === 'asr-sc-good' ? ['#93c5fd', '#1d4ed8']
                                            : colorClass === 'asr-sc-average' ? ['#fde68a', '#d97706']
                                            : colorClass === 'asr-sc-poor' ? ['#fca5a5', '#dc2626'] : ['#e2e8f0', '#94a3b8'];
                                          
                                          return (
                                            <div key={areaObj.name} className={`asr-gauge-cell ${meetsTarget ? 'asr-target-met' : ''}`}>
                                              <span className="asr-gauge-label">{areaObj.name.substring(0, 3)}</span>
                                              <div className={`asr-gauge ${colorClass}`}>
                                                <svg viewBox="0 0 40 40" className="asr-gauge-svg">
                                                  <defs>
                                                    <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
                                                      <stop offset="0%" stopColor={gs} />
                                                      <stop offset="100%" stopColor={ge} />
                                                    </linearGradient>
                                                  </defs>
                                                  <circle cx="20" cy="20" r={radius} className="asr-gauge-track" />
                                                  {score && (
                                                    <circle cx="20" cy="20" r={radius}
                                                      className="asr-gauge-fill"
                                                      stroke={`url(#${gradId})`}
                                                      strokeDasharray={circ}
                                                      strokeDashoffset={offset}
                                                    />
                                                  )}
                                                </svg>
                                                <span className={`asr-gauge-val ${colorClass}`}>{score || '—'}</span>
                                              </div>
                                            </div>
                                          );
                                        }) : (
                                          <span className="asr-no-scores-msg">Pending first assessment</span>
                                        )}
                                      </div>

                                      {/* Actions */}
                                      <div className="asr-actions">
                                        <button
                                          className="asr-btn-history"
                                          onClick={(e) => { e.stopPropagation(); fetchPlayerHistory(player.id); }}
                                          title="View History"
                                        >
                                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                        </button>
                                        <button
                                          className="asr-btn-assess"
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            await handlePlayerSelect(player.id);
                                            setActiveTab('new-assessment');
                                          }}
                                          title="New Assessment"
                                        >
                                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Player History Modal */}
            {showHistoryModal && selectedPlayerHistory && (
              <div className="modal-overlay" onClick={() => setShowHistoryModal(false)}>
                <div className="modal-content history-modal-compact" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header history-header-compact">
                    <div className="header-content-compact">
                      <div className="player-info-compact">
                        <div className="player-avatar-compact">
                          {selectedPlayerHistory.player_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div className="player-details-compact">
                          <h3>{selectedPlayerHistory.player_name || 'Unknown Player'}</h3>
                          <span className="assessment-count">{selectedPlayerHistory.assessments?.length || 0} assessments</span>
                        </div>
                      </div>
                      <div className="score-legend-compact">
                        <span className="legend-dot excellent" title="Excellent (8-10)"></span>
                        <span className="legend-dot good" title="Good (6-7)"></span>
                        <span className="legend-dot average" title="Average (4-5)"></span>
                        <span className="legend-dot poor" title="Needs Work (0-3)"></span>
                      </div>
                    </div>
                    <button className="modal-close-compact" onClick={() => setShowHistoryModal(false)}>×</button>
                  </div>
                  
                  <div className="modal-body history-body-compact">
                    {selectedPlayerHistory.assessments && selectedPlayerHistory.assessments.length > 0 ? (
                      <div className="assessments-timeline">
                        <h4 className="timeline-title">Assessment History</h4>
                        <div className="assessments-list">
                          {selectedPlayerHistory.assessments.map((assessment, index) => (
                            <div key={index} className="assessment-item-compact">
                              <div className="assessment-header-compact">
                                <div className="assessment-date-compact">
                                  <span className="date-compact">
                                    {new Date(assessment.assessment_date).toLocaleDateString('en-US', { 
                                      month: 'short', 
                                      day: 'numeric',
                                      year: '2-digit'
                                    })}
                                  </span>
                                </div>
                                <div className="assessment-info-compact">
                                  <span className="assessment-type-compact">{(assessment.assessment_type || 'REGULAR').toUpperCase()}</span>
                                  <span className="assessor-compact">by {assessment.assessor_name}</span>
                                </div>
                              </div>
                              
                              <div className="scores-row-compact">
                                {(performanceAreas.map(area => area.name) || ['Attitude', 'Batting', 'Bowling', 'Fielding', 'Fitness', 'Wicketkeeping']).map(areaName => {
                                  const areaScores = assessment.detailed_scores ? 
                                    assessment.detailed_scores.filter(score => score.area === areaName) : [];
                                  const averageScore = areaScores.length > 0 ? 
                                    (areaScores.reduce((sum, score) => sum + score.score, 0) / areaScores.length).toFixed(1) : null;
                                  
                                  return (
                                    <div key={areaName} className="score-compact">
                                      <span className="area-label-compact">{areaName.substring(0, 3)}</span>
                                      {averageScore ? (
                                        <span className={`score-badge-compact ${getScoreClass(averageScore)}`}>
                                          {averageScore}
                                        </span>
                                      ) : (
                                        <span className="score-badge-compact score-na">-</span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                              
                              {assessment.overall_comments && (
                                <div className="comments-compact">
                                  <span className="comment-text">{assessment.overall_comments}</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="no-history-compact">
                        <div className="no-history-icon">📭</div>
                        <h3>No Assessment History</h3>
                        <p>This player doesn't have any assessment records yet.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            </>
            )}
          </div>
        )}

        {activeTab === 'players-role' && <PlayersRole />}

        {activeTab === 'calendar' && (
          <div className="tab-content">
            <CoachCalendar />
          </div>
        )}

        {activeTab === 'cric-ai' && (
          <div className="tab-content">
            <CricAITab user={user} players={allPlayers} />
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="tab-content">
            {tabLoading ? (
              <div className="tab-loader">
                <div className="tab-loader-bar" />
                <div className="tab-skel">
                  <div className="tab-skel-hero" />
                  <div className="tab-skel-rows">
                    {[1,2,3,4,5].map(i => <div key={i} className="tab-skel-row" />)}
                  </div>
                </div>
              </div>
            ) : (
            <>
            {/* Settings Navigation */}
            <div className="settings-nav">
              <div className="settings-nav-title">
                <span className="settings-nav-title-icon">⚙️</span>
                <span>Settings</span>
              </div>
              <div className="settings-nav-tabs">
                <button 
                  className={`settings-nav-btn config ${activeSettingsTab === 'performance-management' ? 'active' : ''}`}
                  onClick={() => setActiveSettingsTab('performance-management')}
                >
                  <span className="settings-nav-icon">⚙️</span>
                  <span className="settings-nav-label">Configuration</span>
                </button>
                {user?.user_type !== 'coach' && (
                  <button 
                    className={`settings-nav-btn services ${activeSettingsTab === 'services-management' ? 'active' : ''}`}
                    onClick={() => setActiveSettingsTab('services-management')}
                  >
                    <span className="settings-nav-icon">🏏</span>
                    <span className="settings-nav-label">Services</span>
                  </button>
                )}
                {user?.user_type !== 'coach' && (
                  <button 
                    className={`settings-nav-btn members ${activeSettingsTab === 'members-management' ? 'active' : ''}`}
                    onClick={() => setActiveSettingsTab('members-management')}
                  >
                    <span className="settings-nav-icon">👥</span>
                    <span className="settings-nav-label">Members</span>
                  </button>
                )}
              </div>
            </div>

            {/* Performance Configuration - Unified View */}
            {activeSettingsTab === 'performance-management' && (
              <PerformanceConfig />
            )}

            {/* Services Management */}
            {user?.user_type !== 'coach' && activeSettingsTab === 'services-management' && (
              <ServicesConfig />
            )}

            {/* Members Management - Unified View */}
            {user?.user_type !== 'coach' && activeSettingsTab === 'members-management' && (
              <div className="mbr-page">
                {/* Hero banner */}
                <div className="mbr-hero">
                  <div className="mbr-hero-shine" />
                  <div className="mbr-hero-content">
                    <div className="mbr-hero-left">
                      <div className="mbr-hero-badge">Team Management</div>
                      <h2 className="mbr-hero-title">Members</h2>
                    </div>
                    <div className="mbr-hero-mid">
                      <div className="mbr-stat">
                        <span className="mbr-stat-num">{allUsers.length + allKids.length}</span>
                        <span className="mbr-stat-label">Total</span>
                      </div>
                      <div className="mbr-stat-divider" />
                      <div className="mbr-stat">
                        <span className="mbr-stat-num">{allKids.filter(k => k.is_approved && !k.is_deleted).length}</span>
                        <span className="mbr-stat-label">Players</span>
                      </div>
                      <div className="mbr-stat-divider" />
                      <div className="mbr-stat">
                        <span className="mbr-stat-num">{allKids.filter(k => !k.is_approved && !k.is_deleted).length + pendingUsers.filter(u => !u.is_deleted).length}</span>
                        <span className="mbr-stat-label">Pending</span>
                      </div>
                      <div className="mbr-stat-divider" />
                      <div className="mbr-stat">
                        <span className="mbr-stat-num">{allUsers.filter(u => u.is_deleted).length + allKids.filter(k => k.is_deleted).length}</span>
                        <span className="mbr-stat-label">Deactivated</span>
                      </div>
                    </div>
                    <div className="mbr-hero-actions">
                      <button className="mbr-add-btn" onClick={() => { setEditingItem(null); setModalType('user'); setShowModal(true); }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4-4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
                        Add User
                      </button>
                      <button className="mbr-add-kid" onClick={() => { setEditingItem(null); setModalType('kid'); setShowModal(true); }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                        Add Player
                      </button>
                    </div>
                  </div>
                </div>

                {/* Search bar */}
                <div className="mbr-search-wrap">
                  <svg className="mbr-search-icon" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"/></svg>
                  <input
                    type="text"
                    placeholder="Search members..."
                    value={membersSearchTerm}
                    onChange={(e) => setMembersSearchTerm(e.target.value)}
                    className="mbr-search-input"
                  />
                  {membersSearchTerm && (
                    <button onClick={() => setMembersSearchTerm('')} className="mbr-search-clear">✕</button>
                  )}
                </div>

                {/* Filter chips */}
                <div className="mbr-filters">
                  {[
                    { key: 'all', label: 'All', icon: '👥' },
                    { key: 'pending', label: `Pending`, count: allKids.filter(k => !k.is_approved && !k.is_deleted).length + pendingUsers.filter(u => !u.is_deleted).length, icon: '⏳' },
                    { key: 'players', label: 'Players', icon: '🏏' },
                    { key: 'users', label: 'Users', icon: '👤' },
                    { key: 'coaches', label: 'Coaches', icon: '🎽' },
                    { key: 'admins', label: 'Admins', icon: '👑' },
                    { key: 'no-waiver', label: `No Waiver`, count: allUsers.filter(u => u.user_type === 'parent' && !u.waiver_signed).length, icon: '📋' },
                    { key: 'deactivated', label: 'Deactivated', count: allUsers.filter(u => u.is_deleted).length + allKids.filter(k => k.is_deleted).length, icon: '🚫' },
                  ].map(f => (
                    <button
                      key={f.key}
                      className={`mbr-chip ${membersFilter === f.key ? 'active' : ''}`}
                      onClick={() => setMembersFilter(f.key)}
                    >
                      <span className="mbr-chip-icon">{f.icon}</span>
                      {f.label}
                      {f.count > 0 && <span className="mbr-chip-count">{f.count}</span>}
                    </button>
                  ))}
                </div>

                {/* Members List */}
                <div className="mbr-list">
                  {(() => {
                    const searchLower = membersSearchTerm.toLowerCase();
                    const matchesSearch = (text) => !searchLower || (text || '').toLowerCase().includes(searchLower);

                    // Build parent-kid family map
                    const parentKidsMap = {};
                    allKids.forEach(kid => {
                      const pid = kid.parent_id;
                      if (!parentKidsMap[pid]) parentKidsMap[pid] = [];
                      parentKidsMap[pid].push(kid);
                    });

                    // Collect rendered rows
                    let rows = [];
                    const show = membersFilter;

                    // === PENDING SECTION ===
                    if (show === 'all' || show === 'pending') {
                      const pendingKids = allKids.filter(k => !k.is_approved && !k.is_deleted).filter(k => {
                        const fn = `${k.first_name || ''} ${k.last_name || ''}`;
                        const pn = `${k.parent_first_name || ''} ${k.parent_last_name || ''}`;
                        return matchesSearch(fn) || matchesSearch(k.parent_email) || matchesSearch(pn) || matchesSearch(k.skill_category_name);
                      });
                      const pendingUsersList = pendingUsers.filter(u => {
                        const fn = `${u.first_name || ''} ${u.last_name || ''}`;
                        return matchesSearch(fn) || matchesSearch(u.email) || matchesSearch(u.phone);
                      });

                      if (pendingKids.length > 0 || pendingUsersList.length > 0) {
                        rows.push({ type: 'section-header', label: '⏳ Pending Approval', count: pendingKids.length + pendingUsersList.length });

                        const pendingByParent = {};
                        const pendingOrphans = [];
                        pendingKids.forEach(kid => {
                          const pid = kid.parent_id;
                          const parentExists = allUsers.some(u => u.id === pid);
                          if (pid && parentExists) {
                            if (!pendingByParent[pid]) pendingByParent[pid] = [];
                            pendingByParent[pid].push(kid);
                          } else {
                            pendingOrphans.push(kid);
                          }
                        });

                        Object.keys(pendingByParent).forEach(pid => {
                          const parent = allUsers.find(u => u.id === parseInt(pid));
                          if (parent) rows.push({ type: 'parent-header', data: parent, kidCount: pendingByParent[pid].length, pending: true });
                          pendingByParent[pid].sort((a, b) => (a.first_name || '').localeCompare(b.first_name || ''));
                          pendingByParent[pid].forEach(kid => rows.push({ type: 'kid', data: kid, pending: true, nested: !!parent }));
                        });

                        pendingOrphans.forEach(kid => rows.push({ type: 'kid', data: kid, pending: true }));
                        const shownParentIds = new Set(Object.keys(pendingByParent).map(id => parseInt(id)));
                        pendingUsersList.filter(u => !shownParentIds.has(u.id)).forEach(u => rows.push({ type: 'user', data: u, pending: true }));
                      }
                    }

                    // === PLAYERS SECTION ===
                    if (show === 'all' || show === 'players') {
                      const approvedKids = allKids.filter(k => k.is_approved && !k.is_deleted);
                      const parentIds = new Set(allUsers.filter(u => u.user_type === 'parent').map(u => u.id));
                      const playerRows = [];

                      const parents = allUsers.filter(u => u.user_type === 'parent' && !u.is_deleted).sort((a, b) => (new Date(b.created_at || 0)) - (new Date(a.created_at || 0)));
                      parents.forEach(parent => {
                        const kids = (parentKidsMap[parent.id] || []).filter(k => k.is_approved);
                        if (kids.length === 0) return;
                        const parentName = `${parent.first_name || ''} ${parent.last_name || ''}`;
                        const kidNames = kids.map(k => `${k.first_name || ''} ${k.last_name || ''}`);
                        const kidSkills = kids.map(k => k.skill_category_name || '');
                        const allSearchable = [parentName, parent.email, parent.phone, ...kidNames, ...kidSkills];
                        if (!allSearchable.some(t => matchesSearch(t))) return;

                        playerRows.push({ type: 'parent-header', data: parent, kidCount: kids.length });
                        kids.sort((a, b) => (a.first_name || '').localeCompare(b.first_name || ''));
                        kids.forEach(kid => playerRows.push({ type: 'kid', data: kid, nested: true }));
                      });

                      approvedKids.filter(k => !parentIds.has(k.parent_id)).forEach(kid => {
                        const fn = `${kid.first_name || ''} ${kid.last_name || ''}`;
                        if (matchesSearch(fn) || matchesSearch(kid.parent_email) || matchesSearch(kid.skill_category_name)) {
                          playerRows.push({ type: 'kid', data: kid });
                        }
                      });

                      if (playerRows.length > 0) {
                        rows.push({ type: 'section-header', label: '🏏 Players', count: approvedKids.length });
                        rows.push(...playerRows);
                      }
                    }

                    // === USERS SECTION ===
                    if (show === 'all' || show === 'users') {
                      const shownAsParentHeader = new Set(rows.filter(r => r.type === 'parent-header').map(r => r.data.id));
                      const parentUsers = allUsers.filter(u => u.user_type === 'parent' && !shownAsParentHeader.has(u.id) && !u.is_deleted).filter(u => {
                        const fn = `${u.first_name || ''} ${u.last_name || ''}`;
                        return matchesSearch(fn) || matchesSearch(u.email) || matchesSearch(u.phone);
                      }).sort((a, b) => (new Date(b.created_at || 0)) - (new Date(a.created_at || 0)));

                      if (parentUsers.length > 0) {
                        rows.push({ type: 'section-header', label: '👤 Users', count: parentUsers.length });
                        parentUsers.forEach(u => {
                          const hasKids = (parentKidsMap[u.id] || []).length > 0;
                          rows.push({ type: 'user', data: u, noPlayers: !hasKids });
                        });
                      }
                    }

                    // === COACHES SECTION ===
                    if (show === 'all' || show === 'coaches') {
                      const coachList = allUsers.filter(u => u.user_type === 'coach' && !u.is_deleted).filter(u => {
                        const fn = `${u.first_name || ''} ${u.last_name || ''}`;
                        return matchesSearch(fn) || matchesSearch(u.email) || matchesSearch(u.phone);
                      }).sort((a, b) => (new Date(b.created_at || 0)) - (new Date(a.created_at || 0)));

                      if (coachList.length > 0) {
                        rows.push({ type: 'section-header', label: '🎽 Coaches', count: coachList.length });
                        coachList.forEach(u => rows.push({ type: 'user', data: u }));
                      }
                    }

                    // === ADMINS SECTION ===
                    if (show === 'all' || show === 'admins') {
                      const admins = allUsers.filter(u => u.user_type === 'admin' && !u.is_deleted).filter(u => {
                        const fn = `${u.first_name || ''} ${u.last_name || ''}`;
                        return matchesSearch(fn) || matchesSearch(u.email) || matchesSearch(u.phone);
                      }).sort((a, b) => (new Date(b.created_at || 0)) - (new Date(a.created_at || 0)));

                      if (admins.length > 0) {
                        rows.push({ type: 'section-header', label: '👑 Admins', count: admins.length });
                        admins.forEach(u => rows.push({ type: 'user', data: u }));
                      }
                    }

                    // === NO WAIVER ===
                    if (show === 'no-waiver') {
                      const noWaiverParents = allUsers.filter(u => u.user_type === 'parent' && !u.waiver_signed).filter(u => {
                        const fn = `${u.first_name || ''} ${u.last_name || ''}`;
                        return matchesSearch(fn) || matchesSearch(u.email) || matchesSearch(u.phone);
                      }).sort((a, b) => (new Date(b.created_at || 0)) - (new Date(a.created_at || 0)));

                      if (noWaiverParents.length > 0) {
                        rows.push({ type: 'section-header', label: '📋 No Waiver Signed', count: noWaiverParents.length });
                        noWaiverParents.forEach(parent => {
                          const kids = (parentKidsMap[parent.id] || []);
                          if (kids.length > 0) {
                            rows.push({ type: 'parent-header', data: parent, kidCount: kids.length });
                            kids.sort((a, b) => (a.first_name || '').localeCompare(b.first_name || ''));
                            kids.forEach(kid => rows.push({ type: 'kid', data: kid, nested: true }));
                          } else {
                            rows.push({ type: 'user', data: parent, noPlayers: true });
                          }
                        });
                      }
                    }

                    // === DEACTIVATED SECTION (always at bottom) ===
                    if (show === 'all' || show === 'deactivated') {
                      const deactivatedUsers = allUsers.filter(u => u.is_deleted).filter(u => {
                        const fn = `${u.first_name || ''} ${u.last_name || ''}`;
                        return matchesSearch(fn) || matchesSearch(u.email) || matchesSearch(u.phone);
                      }).sort((a, b) => (new Date(b.created_at || 0)) - (new Date(a.created_at || 0)));

                      const deactivatedKids = allKids.filter(k => k.is_deleted).filter(k => {
                        const fn = `${k.first_name || ''} ${k.last_name || ''}`;
                        const pn = `${k.parent_first_name || ''} ${k.parent_last_name || ''}`;
                        return matchesSearch(fn) || matchesSearch(k.parent_email) || matchesSearch(pn) || matchesSearch(k.skill_category_name);
                      });

                      if (deactivatedUsers.length > 0 || deactivatedKids.length > 0) {
                        rows.push({ type: 'section-header', label: '🚫 Deactivated', count: deactivatedUsers.length + deactivatedKids.length });
                        deactivatedUsers.forEach(u => rows.push({ type: 'user', data: u }));
                        deactivatedKids.forEach(kid => rows.push({ type: 'kid', data: kid }));
                      }
                    }

                    if (rows.length === 0) {
                      return (
                        <div className="mbr-empty">
                          <span className="mbr-empty-icon">🔍</span>
                          <p>No members found{membersSearchTerm ? ` matching "${membersSearchTerm}"` : ''}</p>
                        </div>
                      );
                    }

                    return rows.map((item, idx) => {
                      if (item.type === 'section-header') {
                        return (
                          <div key={`section-${item.label}`} className={`mbr-section-hdr ${idx > 0 ? 'mbr-section-gap' : ''}`}>
                            <span className="mbr-section-label">{item.label}</span>
                            <span className="mbr-section-badge">{item.count}</span>
                          </div>
                        );
                      } else if (item.type === 'parent-header') {
                        const u = item.data;
                        const isDeactivated = u.is_deleted;
                        const isPending = !isDeactivated && (item.pending || u.status === 'pending' || !u.is_approved);
                        const isExpanded = expandedMemberRows[`user-${u.id}`];
                        return (
                          <div key={`user-${u.id}`} className={`mbr-card mbr-card-parent ${isPending ? 'mbr-card-pending' : ''} ${isDeactivated ? 'mbr-card-deactivated' : ''}`}>
                            <div className="mbr-card-main" onClick={() => setExpandedMemberRows(prev => ({...prev, [`user-${u.id}`]: !prev[`user-${u.id}`]}))}>
                              <div className="mbr-avatar mbr-av-parent">
                                {u.profile_pic_url ? <img src={resolveProfilePicUrl(u.profile_pic_url)} alt="" /> : '👤'}
                              </div>
                              <div className="mbr-card-info">
                                <div className="mbr-card-name">{u.first_name} {u.last_name}</div>
                                <div className="mbr-card-meta">
                                  <span className="mbr-pill mbr-pill-parent">Parent</span>
                                  <span className="mbr-card-email">{u.email}</span>
                                  {item.kidCount > 0 && <span className="mbr-pill mbr-pill-kids">{item.kidCount} kid{item.kidCount > 1 ? 's' : ''}</span>}
                                  {!u.waiver_signed && <span className="mbr-pill mbr-pill-warn">No Waiver</span>}
                                  {u.wallet_balance !== undefined && (
                                    <span className={`mbr-pill mbr-wallet-badge ${parseFloat(u.wallet_balance || 0) < 0 ? 'mbr-wallet-negative' : u.wallet_balance > 0 ? 'mbr-wallet-positive' : 'mbr-wallet-zero'}`}>
                                      💰 ${parseFloat(u.wallet_balance || 0).toFixed(2)}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="mbr-card-right">
                                <span className={`mbr-status ${isDeactivated ? 'mbr-status-deactivated' : isPending ? 'mbr-status-pending' : 'mbr-status-active'}`}>
                                  {isDeactivated ? 'Deactivated' : isPending ? 'Pending' : 'Active'}
                                </span>
                                <div className="mbr-card-btns">
                                  {isPending && <button className="mbr-btn-icon mbr-btn-approve" onClick={(e) => {e.stopPropagation(); handleApproveUser(u.id);}} title="Approve">✓</button>}
                                  <button className="mbr-btn-icon mbr-btn-edit" onClick={(e) => {e.stopPropagation(); setEditingItem(u); setModalType('user'); setShowModal(true);}} title="Edit">✏️</button>
                                  <button className="mbr-btn-icon mbr-btn-del" onClick={(e) => {e.stopPropagation(); handleDeleteUser(u.id);}} title="Delete">🗑</button>
                                </div>
                              </div>
                            </div>
                            {isExpanded && (
                              <div className="mbr-card-expand">
                                <div className="mbr-detail-grid">
                                  <div className="mbr-detail"><span className="mbr-detail-lbl">Email</span><span>{u.email}</span></div>
                                  <div className="mbr-detail"><span className="mbr-detail-lbl">Phone</span><span>{u.phone || '—'}</span></div>
                                  <div className="mbr-detail"><span className="mbr-detail-lbl">Joined</span><span>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</span></div>
                                  <div className="mbr-detail"><span className="mbr-detail-lbl">Verified</span><span>{u.email_verified ? '✓ Yes' : '✗ No'}</span></div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      } else if (item.type === 'kid') {
                        const kid = item.data;
                        const isDeactivated = kid.is_deleted;
                        const isPending = !isDeactivated && !kid.is_approved;
                        const isExpanded = expandedMemberRows[`kid-${kid.id}`];
                        // Calculate days remaining for payment validity
                        const validUntilDate = kid.valid_until ? new Date(kid.valid_until) : null;
                        const today = new Date();
                        today.setHours(0,0,0,0);
                        const daysRemaining = validUntilDate ? Math.ceil((validUntilDate - today) / (1000 * 60 * 60 * 24)) : null;
                        const paymentClass = daysRemaining === null ? '' : daysRemaining <= 0 ? 'mbr-pay-expired' : daysRemaining <= 20 ? 'mbr-pay-warning' : 'mbr-pay-ok';
                        return (
                          <div key={`kid-${kid.id}`} className={`mbr-card ${item.nested ? 'mbr-card-nested' : ''} ${isPending ? 'mbr-card-pending' : ''} ${isDeactivated ? 'mbr-card-deactivated' : ''}`}>
                            <div className="mbr-card-main" onClick={() => setExpandedMemberRows(prev => ({...prev, [`kid-${kid.id}`]: !prev[`kid-${kid.id}`]}))}>
                              <div className="mbr-avatar mbr-av-kid">
                                {kid.profile_pic_url ? <img src={resolveProfilePicUrl(kid.profile_pic_url)} alt="" /> : (kid.first_name || '?')[0]}
                              </div>
                              <div className="mbr-card-info">
                                <div className="mbr-card-name">
                                  {kid.first_name} {kid.last_name}
                                  {kid.gender && <span className="mbr-card-gender">({kid.gender})</span>}
                                </div>
                                <div className="mbr-card-meta">
                                  <span className="mbr-pill mbr-pill-kid">Player</span>
                                  <span className="mbr-pill">{kid.skill_category_name || 'No level'}</span>
                                  {kid.age && <span className="mbr-card-email">Age {kid.age}</span>}
                                  {!item.nested && <span className="mbr-card-email">{kid.parent_first_name} {kid.parent_last_name}</span>}
                                  {daysRemaining !== null && (
                                    <span className={`mbr-pill mbr-pay-badge ${paymentClass}`}>
                                      #{daysRemaining <= 0 ? `${Math.abs(daysRemaining)}d overdue` : `${daysRemaining}d left`}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="mbr-card-right">
                                <span className={`mbr-status ${isDeactivated ? 'mbr-status-deactivated' : isPending ? 'mbr-status-pending' : 'mbr-status-active'}`}>
                                  {isDeactivated ? 'Deactivated' : isPending ? 'Pending' : 'Active'}
                                </span>
                                <div className="mbr-card-btns">
                                  {isPending && <button className="mbr-btn-icon mbr-btn-approve" onClick={(e) => {e.stopPropagation(); handleApproveKid(kid.id);}} title="Approve">✓</button>}
                                  <button className="mbr-btn-icon mbr-btn-rate" onClick={(e) => {e.stopPropagation(); setRateOverrideKid(kid); setShowRateOverrideModal(true);}} title="Rate Override">💲</button>
                                  <button className="mbr-btn-icon mbr-btn-edit" onClick={(e) => {e.stopPropagation(); setEditingItem(kid); setModalType('kid'); setShowModal(true);}} title="Edit">✏️</button>
                                  <button className="mbr-btn-icon mbr-btn-del" onClick={(e) => {e.stopPropagation(); handleDeleteKid(kid.id);}} title="Deactivate">🗑</button>
                                </div>
                              </div>
                            </div>
                            {isExpanded && (
                              <div className="mbr-card-expand">
                                <div className="mbr-detail-grid">
                                  <div className="mbr-detail"><span className="mbr-detail-lbl">DOB</span><span>{kid.date_of_birth ? new Date(kid.date_of_birth).toLocaleDateString() : '—'}</span></div>
                                  <div className="mbr-detail"><span className="mbr-detail-lbl">Performance</span><span>{kid.performance_level ? kid.performance_level.charAt(0).toUpperCase() + kid.performance_level.slice(1) : 'Regular'}</span></div>
                                  <div className="mbr-detail"><span className="mbr-detail-lbl">Parent Email</span><span>{kid.parent_email || '—'}</span></div>
                                  <div className="mbr-detail"><span className="mbr-detail-lbl">Birth Cert</span><span>{kid.has_birth_certificate ? '✓ Yes' : '✗ No'}</span></div>
                                  <div className="mbr-detail">
                                    <span className="mbr-detail-lbl">Valid Until</span>
                                    <span className={paymentClass ? `mbr-pay-text ${paymentClass}` : ''}>
                                      {kid.valid_until ? new Date(kid.valid_until).toLocaleDateString() : '—'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      } else {
                        const u = item.data;
                        const isDeactivated = u.is_deleted;
                        const isPending = !isDeactivated && (u.status === 'pending' || !u.is_approved);
                        const typeIcon = u.user_type === 'admin' ? '👑' : u.user_type === 'coach' ? '🎽' : '👤';
                        const isExpanded = expandedMemberRows[`user-${u.id}`];
                        return (
                          <div key={`user-${u.id}`} className={`mbr-card ${isPending ? 'mbr-card-pending' : ''} ${isDeactivated ? 'mbr-card-deactivated' : ''}`}>
                            <div className="mbr-card-main" onClick={() => setExpandedMemberRows(prev => ({...prev, [`user-${u.id}`]: !prev[`user-${u.id}`]}))}>
                              <div className={`mbr-avatar mbr-av-${u.user_type}`}>
                                {u.profile_pic_url ? <img src={resolveProfilePicUrl(u.profile_pic_url)} alt="" /> : typeIcon}
                              </div>
                              <div className="mbr-card-info">
                                <div className="mbr-card-name">{u.first_name} {u.last_name}</div>
                                <div className="mbr-card-meta">
                                  <span className={`mbr-pill mbr-pill-${u.user_type}`}>{u.user_type}</span>
                                  <span className="mbr-card-email">{u.email}</span>
                                  {item.noPlayers && <span className="mbr-pill mbr-pill-warn">No Players</span>}
                                  {!u.waiver_signed && u.user_type === 'parent' && <span className="mbr-pill mbr-pill-warn">No Waiver</span>}
                                  {u.user_type === 'parent' && u.wallet_balance !== undefined && (
                                    <span className={`mbr-pill mbr-wallet-badge ${parseFloat(u.wallet_balance || 0) < 0 ? 'mbr-wallet-negative' : u.wallet_balance > 0 ? 'mbr-wallet-positive' : 'mbr-wallet-zero'}`}>
                                      💰 ${parseFloat(u.wallet_balance || 0).toFixed(2)}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="mbr-card-right">
                                <span className={`mbr-status ${isDeactivated ? 'mbr-status-deactivated' : isPending ? 'mbr-status-pending' : 'mbr-status-active'}`}>
                                  {isDeactivated ? 'Deactivated' : isPending ? 'Pending' : 'Active'}
                                </span>
                                <div className="mbr-card-btns">
                                  {isPending && <button className="mbr-btn-icon mbr-btn-approve" onClick={(e) => {e.stopPropagation(); handleApproveUser(u.id);}} title="Approve">✓</button>}
                                  <button className="mbr-btn-icon mbr-btn-edit" onClick={(e) => {e.stopPropagation(); setEditingItem(u); setModalType('user'); setShowModal(true);}} title="Edit">✏️</button>
                                  <button className="mbr-btn-icon mbr-btn-del" onClick={(e) => {e.stopPropagation(); handleDeleteUser(u.id);}} title="Deactivate">🗑</button>
                                </div>
                              </div>
                            </div>
                            {isExpanded && (
                              <div className="mbr-card-expand">
                                <div className="mbr-detail-grid">
                                  <div className="mbr-detail"><span className="mbr-detail-lbl">Email</span><span>{u.email}</span></div>
                                  <div className="mbr-detail"><span className="mbr-detail-lbl">Phone</span><span>{u.phone || '—'}</span></div>
                                  <div className="mbr-detail"><span className="mbr-detail-lbl">Joined</span><span>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</span></div>
                                  <div className="mbr-detail"><span className="mbr-detail-lbl">Verified</span><span>{u.email_verified ? '✓ Yes' : '✗ No'}</span></div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      }
                    });
                  })()}
                </div>
              </div>
            )}
            </>
            )}
          </div>
        )}

        {/* Settings Modal */}
        {showModal && (
          <SettingsModal 
            modalType={modalType}
            editingItem={editingItem}
            onClose={() => {
              setShowModal(false);
              setEditingItem(null);
            }}
            onSave={handleSaveItem}
            performanceAreas={performanceAreas}
            skillLevels={skillLevels}
            allUsers={allUsers.filter(u => u.user_type === 'parent')}
          />
        )}

        {/* Rate Override Modal */}
        {showRateOverrideModal && rateOverrideKid && (
          <RateOverrideModal
            kid={rateOverrideKid}
            onClose={() => { setShowRateOverrideModal(false); setRateOverrideKid(null); }}
          />
        )}
      </main>
      </div>
    </div>
  );
};

// Settings Modal Component
const SettingsModal = ({ modalType, editingItem, onClose, onSave, performanceAreas, skillLevels, allUsers }) => {
  const [formData, setFormData] = useState({});
  // Proration state (kid edit only)
  const [prorationData, setProrationData] = useState(null);
  const [prorationLoading, setProrationLoading] = useState(false);
  const [prorationError, setProrationError] = useState('');
  const [prorationAmount, setProrationAmount] = useState('');
  const [applyProration, setApplyProration] = useState(true);
  const [prorationSaving, setProrationSaving] = useState(false);

  useEffect(() => {
    if (editingItem) {
      // Special handling for performance-question to ensure all required fields
      if (modalType === 'performance-question') {
        setFormData({
          performance_area_id: editingItem.performance_area_id || '',
          question_text: editingItem.question_text || '',
          display_order: editingItem.display_order || 1,
          is_active: editingItem.is_active !== undefined ? editingItem.is_active : true,
          question_type: editingItem.question_type || 'slider',
          max_score: editingItem.max_score || 10
        });
      } else if (modalType === 'user' || modalType === 'coach' || modalType === 'parent') {
        // Special handling for user forms to ensure proper field mapping
        const formDataToSet = {
          first_name: editingItem.first_name || '',
          last_name: editingItem.last_name || '',
          email: editingItem.email || '',
          phone: editingItem.phone || '',
          date_of_birth: editingItem.date_of_birth || '',
          user_type: editingItem.user_type || 'parent',
          is_active: editingItem.is_active !== undefined ? editingItem.is_active : true,
          is_verified: editingItem.email_verified !== undefined ? editingItem.email_verified : editingItem.is_verified !== undefined ? editingItem.is_verified : false,
          is_approved: editingItem.is_approved !== undefined ? editingItem.is_approved : false
        };
        
        setFormData(formDataToSet);
      } else if (modalType === 'kid') {
        // Special handling for kid forms to map separate is_active and is_approved fields
        setFormData({
          first_name: editingItem.first_name || '',
          last_name: editingItem.last_name || '',
          date_of_birth: editingItem.date_of_birth || '',
          gender: editingItem.gender || '',
          skill_category_id: editingItem.skill_category_id || '',
          performance_level: editingItem.performance_level || 'regular',
          parent_id: editingItem.parent_id || '',
          valid_until: editingItem.valid_until || '',
          is_active: editingItem.is_active !== undefined ? editingItem.is_active : false,
          is_approved: editingItem.is_approved !== undefined ? editingItem.is_approved : false
        });
      } else {
        setFormData(editingItem);
      }
    } else {
      // Initialize form based on modal type
      switch (modalType) {
        case 'performance-area':
          setFormData({ name: '', description: '', is_active: true });
          break;
        case 'performance-question':
          // Check if we have a pre-selected area from the context
          const initialData = { 
            performance_area_id: '', 
            question_text: '', 
            display_order: 1, 
            is_active: true, 
            question_type: 'slider', 
            max_score: 10 
          };
          // If editingItem has performance_area_id, it means we're adding to a specific area
          if (editingItem && editingItem.performance_area_id) {
            initialData.performance_area_id = editingItem.performance_area_id;
          }
          setFormData(initialData);
          break;
        case 'skill-goal':
          if (editingItem) {
            setFormData({ 
              skill_level_id: editingItem.skill_level_id || '', 
              performance_area_name: editingItem.performance_area_name || '', 
              performance_area_id: editingItem.performance_area_id || '',
              goal_score: editingItem.goal_score || '',
              skill_level_name: editingItem.skill_level_name || ''
            });
          } else {
            setFormData({ 
              skill_level_id: '', 
              performance_area_name: '', 
              performance_area_id: '',
              goal_score: '',
              skill_level_name: ''
            });
          }
          break;
        case 'coach':
        case 'parent':
        case 'user':
          setFormData({ 
            first_name: '', 
            last_name: '', 
            email: '', 
            phone: '', 
            date_of_birth: '',
            user_type: modalType === 'coach' ? 'coach' : modalType === 'parent' ? 'parent' : 'parent',
            is_active: true,
            is_verified: false,
            is_approved: true
          });
          break;
        case 'kid':
          setFormData({ 
            first_name: '', 
            last_name: '', 
            date_of_birth: '', 
            gender: '', 
            skill_category_id: '', 
            performance_level: 'regular',
            parent_id: '',
            is_active: false,
            is_approved: false
          });
          break;
        default:
          setFormData({});
      }
    }
  }, [modalType, editingItem]);

  const handleSkillLevelChange = async (newSkillCategoryId) => {
    setFormData(prev => ({...prev, skill_category_id: newSkillCategoryId}));
    setProrationData(null);
    setProrationError('');
    setProrationAmount('');

    // Only calculate proration for existing kids with a changed skill level
    if (!editingItem?.id || !newSkillCategoryId || 
        String(newSkillCategoryId) === String(editingItem.skill_category_id)) {
      return;
    }

    try {
      setProrationLoading(true);
      const res = await api.post('/api/kids/calculate-proration', {
        kid_id: editingItem.id,
        new_skill_category_id: parseInt(newSkillCategoryId)
      });
      if (res.data && res.data.proration_amount !== undefined) {
        setProrationData(res.data);
        setProrationAmount(res.data.proration_amount.toString());
        setApplyProration(res.data.proration_amount > 0);
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Could not calculate proration';
      setProrationError(msg);
    } finally {
      setProrationLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Additional validation for performance-question
    if (modalType === 'performance-question') {
      if (!formData.performance_area_id) {
        alert('Please select a performance area');
        return;
      }
      if (!formData.question_text?.trim()) {
        alert('Please enter the question text');
        return;
      }
      if (!formData.question_type) {
        alert('Please select a question type');
        return;
      }
      if (!formData.max_score || formData.max_score < 1) {
        alert('Please enter a valid maximum score');
        return;
      }
    }

    // Handle proration for kid skill level changes
    if (modalType === 'kid' && editingItem?.id && prorationData && applyProration && parseFloat(prorationAmount) > 0) {
      const confirmed = window.confirm(
        `This will charge $${parseFloat(prorationAmount).toFixed(2)} to the parent's wallet for the skill level upgrade.\n\nProceed?`
      );
      if (!confirmed) return;

      try {
        setProrationSaving(true);
        await api.post('/api/kids/apply-proration', {
          kid_id: editingItem.id,
          new_skill_category_id: parseInt(formData.skill_category_id),
          proration_amount: parseFloat(prorationAmount),
          description: prorationData.description
        });
        // Now save the rest of the kid data (with updated skill_category_id already applied by proration endpoint)
        onSave(modalType, formData);
      } catch (err) {
        alert(err.response?.data?.error || 'Failed to apply proration charge');
        setProrationSaving(false);
        return;
      }
    } else {
      onSave(modalType, formData);
    }
  };

  const renderFormFields = () => {
    switch (modalType) {
      case 'performance-area':
        return (
          <>
            <div className="form-group">
              <label>Area Name *</label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_active || false}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                />
                Active
              </label>
            </div>
          </>
        );

      case 'performance-question':
        return (
          <>
            <div className="form-group">
              <label>Performance Area *</label>
              <select
                value={formData.performance_area_id || ''}
                onChange={(e) => setFormData({...formData, performance_area_id: e.target.value})}
                required
              >
                <option value="">Select Area</option>
                {performanceAreas.map(area => (
                  <option key={area.id} value={area.id}>{area.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Question Text *</label>
              <textarea
                value={formData.question_text || ''}
                onChange={(e) => setFormData({...formData, question_text: e.target.value})}
                required
                placeholder="Enter the performance question..."
              />
            </div>
            <div className="form-group">
              <label>Question Type</label>
              <select
                value={formData.question_type || 'slider'}
                onChange={(e) => setFormData({...formData, question_type: e.target.value})}
              >
                <option value="slider">Slider (1-10)</option>
                <option value="rating">Rating Scale</option>
                <option value="boolean">Yes/No</option>
              </select>
            </div>
            <div className="form-group">
              <label>Maximum Score *</label>
              <input
                type="number"
                value={formData.max_score || 10}
                onChange={(e) => setFormData({...formData, max_score: parseFloat(e.target.value)})}
                min="1"
                max="100"
                required
              />
            </div>
            <div className="form-group">
              <label>Display Order *</label>
              <input
                type="number"
                value={formData.display_order || 1}
                onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value)})}
                min="1"
                required
              />
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_active || false}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                />
                Active
              </label>
            </div>
          </>
        );

      case 'skill-goal':
        return (
          <>
            <div className="form-group">
              <label>Skill Level *</label>
              <select
                value={formData.skill_level_id || ''}
                onChange={(e) => {
                  const selectedLevel = skillLevels.find(level => level.id === parseInt(e.target.value));
                  setFormData({
                    ...formData, 
                    skill_level_id: e.target.value,
                    skill_level_name: selectedLevel ? selectedLevel.name : ''
                  });
                }}
                required
                disabled={editingItem && editingItem.skill_level_id}
              >
                <option value="">Select Skill Level</option>
                {skillLevels.map(level => (
                  <option key={level.id} value={level.id}>{level.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Performance Area *</label>
              <select
                value={formData.performance_area_name || ''}
                onChange={(e) => {
                  const selectedArea = performanceAreas.find(area => area.name === e.target.value);
                  setFormData({
                    ...formData, 
                    performance_area_name: e.target.value,
                    performance_area_id: selectedArea ? selectedArea.id : ''
                  });
                }}
                required
                disabled={editingItem && editingItem.performance_area_name}
              >
                <option value="">Select Area</option>
                {performanceAreas.map(area => (
                  <option key={area.id} value={area.name}>{area.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Target Score *</label>
              <input
                type="number"
                value={formData.goal_score || ''}
                onChange={(e) => setFormData({...formData, goal_score: parseFloat(e.target.value)})}
                min="0"
                max="10"
                step="0.1"
                required
              />
            </div>
          </>
        );

      case 'coach':
      case 'parent':
      case 'user':
        return (
          <div className="sleek-user-form">
            {modalType === 'user' && (
              <div className="sleek-field sleek-field-full">
                <label>Role</label>
                <div className="sleek-role-picker">
                  {[{v:'parent',l:'Parent',i:'👨‍👩‍👧‍👦'},{v:'coach',l:'Coach',i:'🎽'},{v:'admin',l:'Admin',i:'⚙️'}].map(r => (
                    <button key={r.v} type="button" className={`sleek-role-chip ${formData.user_type === r.v ? 'active' : ''}`}
                      onClick={() => setFormData({...formData, user_type: r.v})}>
                      <span>{r.i}</span> {r.l}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="sleek-row">
              <div className="sleek-field">
                <label>First Name</label>
                <input type="text" value={formData.first_name || ''} onChange={(e) => setFormData({...formData, first_name: e.target.value})} required placeholder="John" />
              </div>
              <div className="sleek-field">
                <label>Last Name</label>
                <input type="text" value={formData.last_name || ''} onChange={(e) => setFormData({...formData, last_name: e.target.value})} required placeholder="Doe" />
              </div>
            </div>
            <div className="sleek-row">
              <div className="sleek-field">
                <label>Email</label>
                <input type="email" value={formData.email || ''} onChange={(e) => setFormData({...formData, email: e.target.value})} required disabled={editingItem} placeholder="john@example.com" />
              </div>
              <div className="sleek-field">
                <label>Phone</label>
                <input type="tel" value={formData.phone || ''} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="+1 (555) 000-0000" />
              </div>
            </div>
            <div className="sleek-row">
              <div className="sleek-field">
                <label>Date of Birth</label>
                <input type="date" value={formData.date_of_birth || ''} onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})} />
              </div>
              {!editingItem ? (
                <div className="sleek-field">
                  <label>Password <span className="sleek-hint">optional</span></label>
                  <input type="password" value={formData.password || ''} onChange={(e) => setFormData({...formData, password: e.target.value})} placeholder="Auto-generated" />
                </div>
              ) : <div className="sleek-field" />}
            </div>
            <div className="sleek-switches">
              {[{k:'is_active',l:'Active'},{k:'is_verified',l:'Verified'},{k:'is_approved',l:'Approved'}].map(t => (
                <label key={t.k} className={`sleek-switch ${formData[t.k] ? 'on' : ''}`}>
                  <input type="checkbox" checked={formData[t.k] || false} onChange={(e) => setFormData({...formData, [t.k]: e.target.checked})} />
                  <span className="sleek-switch-track"><span className="sleek-switch-thumb" /></span>
                  <span className="sleek-switch-label">{t.l}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 'kid':
        return (
          <div className="sleek-user-form">
            <div className="sleek-row">
              <div className="sleek-field">
                <label>First Name</label>
                <input type="text" value={formData.first_name || ''} onChange={(e) => setFormData({...formData, first_name: e.target.value})} required placeholder="First name" />
              </div>
              <div className="sleek-field">
                <label>Last Name</label>
                <input type="text" value={formData.last_name || ''} onChange={(e) => setFormData({...formData, last_name: e.target.value})} required placeholder="Last name" />
              </div>
            </div>
            <div className="sleek-row">
              <div className="sleek-field">
                <label>Date of Birth</label>
                <input type="date" value={formData.date_of_birth || ''} onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})} required />
              </div>
              <div className="sleek-field">
                <label>Gender</label>
                <select value={formData.gender || ''} onChange={(e) => setFormData({...formData, gender: e.target.value})}>
                  <option value="">Select</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div className="sleek-row">
              <div className="sleek-field">
                <label>Skill Level</label>
                <select value={formData.skill_category_id || ''} onChange={(e) => handleSkillLevelChange(e.target.value)} required>
                  <option value="">Select Level</option>
                  {skillLevels.map(level => (
                    <option key={level.id} value={level.id}>{level.name}</option>
                  ))}
                </select>
              </div>
              <div className="sleek-field">
                <label>Performance</label>
                <div className="sleek-role-picker">
                  {[{v:'regular',l:'Regular'},{v:'high',l:'High'}].map(p => (
                    <button key={p.v} type="button" className={`sleek-role-chip ${formData.performance_level === p.v ? 'active' : ''}`}
                      onClick={() => setFormData({...formData, performance_level: p.v})}>
                      {p.l}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {/* Proration Section */}
            {editingItem && prorationLoading && (
              <div className="proration-section proration-loading">
                <span className="proration-spinner"></span> Calculating proration...
              </div>
            )}
            {editingItem && prorationError && (
              <div className="proration-section proration-info">
                <span className="proration-info-icon">ℹ️</span> {prorationError}
              </div>
            )}
            {editingItem && prorationData && prorationData.proration_amount !== 0 && (
              <div className="proration-section proration-card">
                <div className="proration-header">
                  <span className="proration-icon">💰</span>
                  <span className="proration-title">Proration Charge</span>
                </div>
                <div className="proration-details">
                  <div className="proration-detail-row">
                    <span className="proration-label">Level Change</span>
                    <span className="proration-value">{prorationData.current_skill_name} → {prorationData.new_skill_name}</span>
                  </div>
                  <div className="proration-detail-row">
                    <span className="proration-label">Plan</span>
                    <span className="proration-value">{prorationData.rate_type_label}{prorationData.discount_type === 'percent' ? ` (${prorationData.discount_value}% discount)` : ''}</span>
                  </div>
                  <div className="proration-detail-row">
                    <span className="proration-label">Remaining</span>
                    <span className="proration-value">{prorationData.remaining_months} month{prorationData.remaining_months !== 1 ? 's' : ''} (until {prorationData.valid_until})</span>
                  </div>
                  <div className="proration-detail-row">
                    <span className="proration-label">Old Rate</span>
                    <span className="proration-value">${prorationData.old_monthly_rate}/mo (${prorationData.old_effective_total} total)</span>
                  </div>
                  <div className="proration-detail-row">
                    <span className="proration-label">New Rate</span>
                    <span className="proration-value">${prorationData.new_monthly_rate}/mo (${prorationData.new_effective_total} total)</span>
                  </div>
                </div>
                <div className="proration-amount-section">
                  <div className="proration-amount-row">
                    <label className="proration-amount-label">
                      <input type="checkbox" checked={applyProration} onChange={(e) => setApplyProration(e.target.checked)} />
                      Charge to parent's wallet
                    </label>
                  </div>
                  {applyProration && (
                    <div className="proration-input-row">
                      <span className="proration-input-prefix">$</span>
                      <input
                        type="number"
                        className="proration-input"
                        value={prorationAmount}
                        onChange={(e) => setProrationAmount(e.target.value)}
                        min="0"
                        step="0.01"
                      />
                      {String(prorationAmount) !== String(prorationData.proration_amount) && (
                        <button type="button" className="proration-reset-btn" onClick={() => setProrationAmount(prorationData.proration_amount.toString())} title="Reset to calculated amount">↺</button>
                      )}
                    </div>
                  )}
                  {applyProration && (
                    <div className="proration-note">
                      This amount will be deducted from the parent's wallet balance. If insufficient, the balance will go negative (amount owed).
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="sleek-row">
              <div className="sleek-field">
                <label>Parent</label>
                <select value={formData.parent_id || ''} onChange={(e) => setFormData({...formData, parent_id: e.target.value})} required>
                  <option value="">Select Parent</option>
                  {allUsers.map(user => (
                    <option key={user.id} value={user.id}>{user.first_name} {user.last_name} ({user.email})</option>
                  ))}
                </select>
              </div>
              <div className="sleek-field">
                <label>Valid Until</label>
                <input type="date" value={formData.valid_until || ''} onChange={(e) => setFormData({...formData, valid_until: e.target.value})} />
              </div>
            </div>
            <div className="sleek-switches">
              {[{k:'is_active',l:'Active'},{k:'is_approved',l:'Approved'}].map(t => (
                <label key={t.k} className={`sleek-switch ${formData[t.k] ? 'on' : ''}`}>
                  <input type="checkbox" checked={formData[t.k] || false} onChange={(e) => setFormData({...formData, [t.k]: e.target.checked})} />
                  <span className="sleek-switch-track"><span className="sleek-switch-thumb" /></span>
                  <span className="sleek-switch-label">{t.l}</span>
                </label>
              ))}
            </div>
          </div>
        );

      default:
        return <div>Unknown form type</div>;
    }
  };

  const getModalMeta = () => {
    const meta = {
      'performance-area': { title: 'Performance Area', icon: '📊', accent: '#6366f1' },
      'performance-question': { title: 'Performance Question', icon: '❓', accent: '#8b5cf6' },
      'skill-goal': { title: 'Skill Level Goal', icon: '🎯', accent: '#f59e0b' },
      'coach': { title: 'Coach', icon: '🎽', accent: '#10b981' },
      'parent': { title: 'Parent', icon: '👨‍👩‍👧‍👦', accent: '#3b82f6' },
      'user': { title: 'User', icon: '👤', accent: '#3b82f6' },
      'kid': { title: 'Player', icon: '🏏', accent: '#f97316' }
    };
    return meta[modalType] || { title: 'Item', icon: '📝', accent: '#3b82f6' };
  };

  const { title, icon, accent } = getModalMeta();

  return (
    <div className="modal-overlay sleek-overlay" onClick={onClose}>
      <div className="modal-content settings-modal sleek-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sleek-modal-header" style={{ '--accent': accent }}>
          <div className="sleek-header-left">
            <span className="sleek-header-icon">{icon}</span>
            <div>
              <h3 className="sleek-header-title">{editingItem ? 'Edit' : 'New'} {title}</h3>
              <p className="sleek-header-sub">{editingItem ? 'Update the details below' : 'Fill in the details to create'}</p>
            </div>
          </div>
          <button className="sleek-close" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="modal-body sleek-modal-body">
          {renderFormFields()}
          
          <div className="sleek-modal-footer">
            <button type="button" className="sleek-btn sleek-btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="sleek-btn sleek-btn-submit" style={{ '--accent': accent }} disabled={prorationSaving || prorationLoading}>
              {prorationSaving ? '⏳ Applying...' : editingItem ? '✓ Update' : '+ Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Rate Override Modal Component
const RateOverrideModal = ({ kid, onClose }) => {
  const [overrides, setOverrides] = useState([]);
  const [services, setServices] = useState({ categories: [], items: [], rates: [], lookups: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingOverride, setEditingOverride] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [addingRate, setAddingRate] = useState(null);
  const [newAmount, setNewAmount] = useState('');
  const [newNotes, setNewNotes] = useState('');

  useEffect(() => {
    fetchData();
  }, [kid.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      setLoading(true);
      const [overridesRes, servicesRes] = await Promise.all([
        api.get(`/api/kid-rate-overrides/${kid.id}`),
        api.get('/api/services/all')
      ]);
      setOverrides(overridesRes.data.overrides || []);
      setServices(servicesRes.data);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (serviceRateId, amount, notes) => {
    try {
      setSaving(serviceRateId);
      setError('');
      await api.post('/api/kid-rate-overrides', {
        kid_id: kid.id,
        service_rate_id: serviceRateId,
        override_amount: parseFloat(amount),
        notes: notes
      });
      setSuccess('Override saved!');
      setEditingOverride(null);
      setAddingRate(null);
      await fetchData();
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save override');
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (overrideId) => {
    if (!window.confirm('Remove this rate override?')) return;
    try {
      setSaving(overrideId);
      await api.delete(`/api/kid-rate-overrides/${overrideId}`);
      setSuccess('Override removed!');
      await fetchData();
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError('Failed to delete override');
    } finally {
      setSaving(null);
    }
  };

  const rateLabels = {};
  (services.lookups?.rate_types || []).forEach(rt => { rateLabels[rt.key] = rt.label; });

  const fmt = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  // Group rates by category > item for the "add" section
  const overriddenRateIds = new Set(overrides.map(o => o.service_rate_id));
  const availableRates = (services.rates || []).filter(r => r.is_active && !overriddenRateIds.has(r.id));
  const itemMap = {};
  (services.items || []).forEach(i => { itemMap[i.id] = i; });
  const catMap = {};
  (services.categories || []).forEach(c => { catMap[c.id] = c; });

  // For group class categories, only show the item matching the kid's assigned class
  const isGroupClassCat = (cat) => {
    const n = (cat.name || '').toLowerCase();
    return n.includes('group') && n.includes('class');
  };
  const getKidMatchItem = (catId) => {
    if (!kid?.skill_category_name) return null;
    const catName = kid.skill_category_name.toLowerCase().trim();
    const catItems = (services.items || []).filter(i => i.category_id === catId && i.is_active !== false);
    const exact = catItems.find(i => (i.name || '').toLowerCase().trim() === catName);
    if (exact) return exact;
    return catItems.find(i => {
      const iName = (i.name || '').toLowerCase().trim();
      return iName.includes(catName) || catName.includes(iName);
    }) || null;
  };

  return (
    <div className="modal-overlay sleek-overlay" onClick={onClose}>
      <div className="sleek-modal rate-override-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sleek-modal-header" style={{ '--accent': '#10b981' }}>
          <div className="sleek-header-left">
            <span className="sleek-header-icon">💲</span>
            <div>
              <h3 className="sleek-header-title">Rate Overrides</h3>
              <p className="sleek-header-sub">{kid.first_name} {kid.last_name}</p>
            </div>
          </div>
          <button className="sleek-close" onClick={onClose}>×</button>
        </div>

        <div className="sleek-modal-body rate-override-body">
          {error && <div className="ro-alert ro-alert-error">{error}</div>}
          {success && <div className="ro-alert ro-alert-success">{success}</div>}

          {loading ? (
            <div className="ro-loading">Loading...</div>
          ) : (
            <>
              {/* Existing Overrides */}
              <div className="ro-section">
                <div className="ro-section-title">
                  <span>Active Overrides</span>
                  <span className="ro-badge">{overrides.length}</span>
                </div>
                {overrides.length === 0 ? (
                  <div className="ro-empty">No rate overrides set. Add one below.</div>
                ) : (
                  <div className="ro-list">
                    {overrides.map(ov => (
                      <div key={ov.id} className="ro-item">
                        <div className="ro-item-info">
                          <div className="ro-item-name">{ov.service_item_name}</div>
                          <div className="ro-item-meta">
                            <span className="ro-pill">{ov.category_name}</span>
                            <span className="ro-pill">{rateLabels[ov.rate_type] || ov.rate_type}</span>
                          </div>
                        </div>
                        {editingOverride === ov.id ? (
                          <div className="ro-edit-form">
                            <div className="ro-edit-row">
                              <div className="ro-price-compare">
                                <span className="ro-default-price">Default: {fmt(ov.default_amount)}</span>
                              </div>
                              <div className="ro-input-group">
                                <span className="ro-input-prefix">$</span>
                                <input
                                  type="number"
                                  className="ro-input"
                                  value={editAmount}
                                  onChange={(e) => setEditAmount(e.target.value)}
                                  min="0"
                                  step="0.01"
                                  placeholder="Override amount"
                                />
                              </div>
                            </div>
                            <input
                              type="text"
                              className="ro-input ro-input-notes"
                              value={editNotes}
                              onChange={(e) => setEditNotes(e.target.value)}
                              placeholder="Notes (optional)"
                              maxLength={500}
                            />
                            <div className="ro-edit-actions">
                              <button className="ro-btn ro-btn-save" disabled={saving === ov.service_rate_id} onClick={() => handleSave(ov.service_rate_id, editAmount, editNotes)}>
                                {saving === ov.service_rate_id ? '...' : 'Save'}
                              </button>
                              <button className="ro-btn ro-btn-cancel" onClick={() => setEditingOverride(null)}>Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <div className="ro-item-right">
                            <div className="ro-item-prices">
                              <span className="ro-price-default">{fmt(ov.default_amount)}</span>
                              <span className="ro-price-arrow">→</span>
                              <span className="ro-price-override">{fmt(ov.override_amount)}</span>
                            </div>
                            {ov.notes && <div className="ro-item-notes">{ov.notes}</div>}
                            <div className="ro-item-actions">
                              <button className="ro-btn-sm ro-btn-edit" onClick={() => { setEditingOverride(ov.id); setEditAmount(ov.override_amount); setEditNotes(ov.notes || ''); }} title="Edit">✏️</button>
                              <button className="ro-btn-sm ro-btn-del" disabled={saving === ov.id} onClick={() => handleDelete(ov.id)} title="Remove">🗑</button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add New Override */}
              <div className="ro-section">
                <div className="ro-section-title">
                  <span>Add Override</span>
                </div>
                {availableRates.length === 0 ? (
                  <div className="ro-empty">All rates already have overrides.</div>
                ) : (
                  <div className="ro-add-list">
                    {(services.categories || []).filter(c => c.is_active !== false).map(cat => {
                      const isGroup = isGroupClassCat(cat);
                      let catItems;
                      if (isGroup) {
                        const matched = getKidMatchItem(cat.id);
                        catItems = matched ? [matched] : [];
                      } else {
                        catItems = (services.items || []).filter(i => i.category_id === cat.id && i.is_active !== false);
                      }
                      const catAvailable = availableRates.filter(r => catItems.some(i => i.id === r.service_item_id));
                      if (catAvailable.length === 0) return null;
                      return (
                        <div key={cat.id} className="ro-cat-group">
                          <div className="ro-cat-name">{cat.name}</div>
                          {catItems.map(item => {
                            const itemRates = catAvailable.filter(r => r.service_item_id === item.id);
                            if (itemRates.length === 0) return null;
                            return (
                              <div key={item.id} className="ro-add-item">
                                <div className="ro-add-item-name">{item.name}</div>
                                <div className="ro-add-rates">
                                  {itemRates.map(rate => (
                                    <div key={rate.id} className="ro-add-rate">
                                      {addingRate === rate.id ? (
                                        <div className="ro-add-form">
                                          <div className="ro-add-form-header">
                                            <span className="ro-pill">{rateLabels[rate.rate_type] || rate.rate_type}</span>
                                            <span className="ro-default-price">Default: {fmt(rate.rate_amount)}</span>
                                          </div>
                                          <div className="ro-input-group">
                                            <span className="ro-input-prefix">$</span>
                                            <input
                                              type="number"
                                              className="ro-input"
                                              value={newAmount}
                                              onChange={(e) => setNewAmount(e.target.value)}
                                              min="0"
                                              step="0.01"
                                              placeholder="Override amount"
                                              autoFocus
                                            />
                                          </div>
                                          <input
                                            type="text"
                                            className="ro-input ro-input-notes"
                                            value={newNotes}
                                            onChange={(e) => setNewNotes(e.target.value)}
                                            placeholder="Notes (optional)"
                                            maxLength={500}
                                          />
                                          <div className="ro-edit-actions">
                                            <button className="ro-btn ro-btn-save" disabled={saving === rate.id || !newAmount} onClick={() => handleSave(rate.id, newAmount, newNotes)}>
                                              {saving === rate.id ? '...' : 'Add Override'}
                                            </button>
                                            <button className="ro-btn ro-btn-cancel" onClick={() => { setAddingRate(null); setNewAmount(''); setNewNotes(''); }}>Cancel</button>
                                          </div>
                                        </div>
                                      ) : (
                                        <button className="ro-add-rate-btn" onClick={() => { setAddingRate(rate.id); setNewAmount(''); setNewNotes(''); }}>
                                          <span className="ro-pill">{rateLabels[rate.rate_type] || rate.rate_type}</span>
                                          <span className="ro-rate-amount">{fmt(rate.rate_amount)}</span>
                                          <span className="ro-add-icon">+</span>
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
